/**
 * OEE Data Service
 * Manages data fetching for OEE metrics, events, and timeline
 */

import { saveAs } from 'file-saver'
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  ActiveTimeEntry, Batch, BatchNameAutocompleteItem, BatchTimeEntry, StationActiveJob, StationComparisonShiftUtilization, StationCompletedJob, StationEfficiencyKpis, ProcessFlowStationWarning,
  CompleteBatchRequest, CreateBatchRequest, StartTimeEntryRequest, WorkerNote
} from '../models/batch.model'
import {
  AnalyticsMetric, CalculationMode, ChartSeries, ParetoCategoryMetric, ParetoDataPoint, ParetoValueMetric, TimeInterval
} from '../models/chart-config.model'
import { BatchCustomFieldValue, Equipment, EquipmentCustomField, EquipmentState, Site, System } from '../models/equipment.model'
import { StationCustomField } from '../models/station.model'
import {
  AbortBatchRequest, AbortBatchResponse, AbortReason, AbortReasonGroup, BatchLifecycleResponse, MarkApprovedRequest, MarkReadyForReviewRequest, UnabortBatchRequest, UnabortBatchResponse,
  ArchiveEquipmentRequest, ArchiveEventRequest, CreateEventRequest, DowntimeEvent, DowntimeReason, DowntimeReasonGroup, DowntimeReasonType, EditEventRequest,
  EventLogResponse, IncidentReason, IncidentReasonGroup, LinkedBatchEvent, LinkedDowntimeIncidentEvent, MachineEvent, MachineState, ResolveErrorRequest, StartTroubleshootingRequest, StateTimelineSegment, UnarchiveEquipmentRequest, UnarchiveEventRequest
} from '../models/machine-event.model'
import { convertFromPPM } from '../models/oee-constants'
import { BatchFocusResponse, BatchHistoryResponse, SiteJobSearchResponse } from '../models/batch-history.model'
import { OEEMetrics, OEEMetricsAdaptive, RateUnit, TimeRangeOption } from '../models/oee-metrics.model'

@Injectable({
    providedIn: 'root'
})
export class OEEDataService {

    /**
     * Selected Site State Management
     */
    private selectedSiteSubject = new BehaviorSubject<string | null>(localStorage.getItem('oee_selected_site_id'));
    public selectedSite$ = this.selectedSiteSubject.asObservable();

    /**
     * Shared Time Range State Management
     * Syncs time range selection across OEE Dashboard and Batch History pages
     */
    private readonly TIME_RANGE_STORAGE_KEY = 'oee_selected_time_range';
    private readonly CUSTOM_RANGE_START_STORAGE_KEY = 'oee_custom_time_range_start';
    private readonly CUSTOM_RANGE_END_STORAGE_KEY = 'oee_custom_time_range_end';
    private selectedTimeRangeSubject = new BehaviorSubject<TimeRangeOption>(
        (localStorage.getItem(this.TIME_RANGE_STORAGE_KEY) as TimeRangeOption) || 'last_7_days'
    );
    public selectedTimeRange$ = this.selectedTimeRangeSubject.asObservable();

    /**
     * Emits after batch time entries are created, stopped, edited, or deleted.
     * Consumers use this to refresh derived state such as station/equipment badges.
     */
    private batchTimeEntriesChangedSubject = new Subject<void>();
    public batchTimeEntriesChanged$ = this.batchTimeEntriesChangedSubject.asObservable();

    /**
     * Available time range options for dropdowns
     */
    public readonly timeRangeOptions: { label: string; value: TimeRangeOption }[] = [
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'Last 24 Hours', value: 'last_24_hours' },
        { label: 'Last 7 Days', value: 'last_7_days' },
        { label: 'Last 30 Days', value: 'last_30_days' },
        { label: 'Last 3 Months', value: 'last_3_months' },
        { label: 'This Week', value: 'this_week' },
        { label: 'This Month', value: 'this_month' },
        { label: 'Last Month', value: 'last_month' },
        { label: 'Custom', value: 'custom' }
    ];

    constructor(
        private http: HttpClient
    ) { }

    /**
     * Set the currently selected site
     * Persists to localStorage and notifies subscribers
     */
    setSelectedSite(siteId: string): void {
        localStorage.setItem('oee_selected_site_id', siteId);
        this.selectedSiteSubject.next(siteId);
    }

    clearSelectedSite(): void {
        localStorage.removeItem('oee_selected_site_id');
        this.selectedSiteSubject.next(null);
    }

    ensureSelectedSite(): Observable<string | null> {
        return this.getSites().pipe(
            map(sites => {
                const cachedSiteId = this.getSelectedSiteId();
                const matchingSite = sites.find(site => site.id === cachedSiteId);

                if (matchingSite) return matchingSite.id;

                const firstSite = sites.find(site => site.is_active !== false) ?? sites[0] ?? null;
                if (firstSite) {
                    this.setSelectedSite(firstSite.id);
                    return firstSite.id;
                }

                this.clearSelectedSite();
                return null;
            })
        );
    }

    /**
     * Get the current selected site ID synchronously
     */
    getSelectedSiteId(): string | null {
        return this.selectedSiteSubject.value;
    }

    /**
     * Get OEE metrics for a specific asset (or list of assets) and time range
     */
    getOEEMetrics(
        assetIds: string | string[],
        startTime: string,
        endTime: string,
        windowType?: 'shift' | 'hour' | 'day' | 'week'
    ): Observable<OEEMetrics> {
        let params = new HttpParams()
            .set('start_time', startTime)
            .set('end_time', endTime);

        if (windowType) {
            params = params.set('window_type', windowType);
        }

        const id = Array.isArray(assetIds) ? assetIds[0] : assetIds;

        return this.http.get<OEEMetrics>(`${environment.api}/oee/metrics/${id}`, { params });
    }

    /**
     * Get OEE metrics with smart adaptive window (Issue #2667)
     * Uses equipment's cycle time to automatically determine appropriate time window.
     * Returns both actual metrics (completed jobs) and expected metrics (including running batches).
     *
     * @param assetId The equipment ID to get metrics for
     * @param startTime Optional start time to constrain the adaptive window (ISO string)
     * @param endTime Optional end time to constrain the adaptive window (ISO string)
     * @returns Observable of adaptive OEE metrics with window info and expected values
     */
    getOEEMetricsAdaptive(
        assetId: string,
        startTime?: string,
        endTime?: string
    ): Observable<OEEMetricsAdaptive> {
        let params = new HttpParams();
        if (startTime) {
            params = params.set('start_time', startTime);
        }
        if (endTime) {
            params = params.set('end_time', endTime);
        }
        return this.http.get<OEEMetricsAdaptive>(
            `${environment.api}/oee/metrics/adaptive/${assetId}`,
            { params }
        );
    }

    /**
     * Aggregate OEE metrics from multiple equipment
     */
    private aggregateOEEMetrics(metricsList: OEEMetrics[], startTime: string, endTime: string): OEEMetrics {
        if (metricsList.length === 0) {
            throw new Error('Cannot aggregate empty metrics list');
        }

        // If only one, return it directly
        if (metricsList.length === 1) {
            return metricsList[0];
        }

        // Sum up absolute values
        const totalParts = metricsList.reduce((sum, m) => sum + m.production.total_parts, 0);
        const goodParts = metricsList.reduce((sum, m) => sum + m.production.good_parts, 0);
        const scrapParts = metricsList.reduce((sum, m) => sum + m.production.scrap_parts, 0);

        const totalDowntime = metricsList.reduce((sum, m) => sum + m.downtime.total_minutes, 0);
        const plannedDowntime = metricsList.reduce((sum, m) => sum + m.downtime.planned_minutes, 0);
        const unplannedDowntime = metricsList.reduce((sum, m) => sum + m.downtime.unplanned_minutes, 0);

        // Calculate weighted averages for OEE components

        // Quality: Total Good / Total Parts
        const quality = totalParts > 0 ? (goodParts / totalParts) * 100 : 0;

        // Availability: (Sum(Available Time) - Sum(Unplanned Downtime)) / Sum(Available Time)
        // We need to calculate Available Time for each machine first
        // Available Time = Total Time (Window) - Planned Downtime
        // Since all metrics are for the same time window, we can calculate window duration once
        const start = new Date(startTime).getTime();
        const end = new Date(endTime).getTime();
        const windowDurationMinutes = (end - start) / (1000 * 60);

        // Sum of Available Time across all machines
        // Available Time_i = Window Duration - Planned Downtime_i
        // Sum(Available Time) = (Window Duration * N) - Sum(Planned Downtime)
        const totalAvailableTime = (windowDurationMinutes * metricsList.length) - plannedDowntime;

        // Modified per user request: Planned downtime should also contribute to unavailability
        // Availability = (Total Time - Planned Downtime - Unplanned Downtime) / Total Time
        const totalTime = windowDurationMinutes * metricsList.length;
        const availability = totalTime > 0
            ? ((totalTime - plannedDowntime - unplannedDowntime) / totalTime) * 100
            : 0;

        // Performance: (Total Parts) / (Theoretical Max Parts)
        // Theoretical Max Parts = Sum(Run Time_i * Target Rate_i)
        // Run Time_i = Available Time_i - Unplanned Downtime_i
        // Target Rate_i = target_rate_ppm (canonical)

        let totalTheoreticalMaxParts = 0;

        metricsList.forEach(m => {
            // Re-calculate run time for this machine
            // Run Time = Window - Planned - Unplanned
            const machineAvailable = windowDurationMinutes - m.downtime.planned_minutes;
            const machineRunTime = machineAvailable - m.downtime.unplanned_minutes;

            // Add to theoretical max
            // Ensure run time is non-negative
            const validRunTime = Math.max(0, machineRunTime);
            totalTheoreticalMaxParts += (validRunTime * m.production.target_rate_ppm);
        });

        const performance = totalTheoreticalMaxParts > 0
            ? (totalParts / totalTheoreticalMaxParts) * 100
            : 0;

        // Overall OEE
        const overall = (availability / 100) * (performance / 100) * (quality / 100) * 100;

        // Rates
        // Sum of actual rates (throughput)
        // We need to be careful with units. We'll convert all to PPM first.
        const totalActualPPM = metricsList.reduce((sum, m) => sum + m.production.actual_rate_ppm, 0);
        const totalTargetPPM = metricsList.reduce((sum, m) => sum + m.production.target_rate_ppm, 0);

        // Use the unit of the first equipment or a default
        const displayUnit = metricsList[0].production.rate_unit;
        const totalActualDisplay = convertFromPPM(totalActualPPM, displayUnit);
        const totalTargetDisplay = convertFromPPM(totalTargetPPM, displayUnit);

        // Calculate average targets from component metrics
        const avgTargetOee = metricsList.reduce((sum, m) => sum + m.targets.oee, 0) / metricsList.length;
        const avgTargetAvailability = metricsList.reduce((sum, m) => sum + m.targets.availability, 0) / metricsList.length;
        const avgTargetPerformance = metricsList.reduce((sum, m) => sum + m.targets.performance, 0) / metricsList.length;
        const avgTargetQuality = metricsList.reduce((sum, m) => sum + m.targets.quality, 0) / metricsList.length;

        return {
            asset_id: 'multiple', // Indicates aggregation
            asset_name: 'Multiple Equipment',
            time_window: {
                start: startTime,
                end: endTime,
                type: 'shift'
            },
            oee: {
                overall: overall,
                availability: availability,
                performance: performance,
                quality: quality
            },
            production: {
                total_parts: totalParts,
                good_parts: goodParts,
                scrap_parts: scrapParts,
                target_rate_ppm: totalTargetPPM,
                actual_rate_ppm: totalActualPPM,
                target_rate: totalTargetDisplay,
                actual_rate: totalActualDisplay,
                rate_unit: displayUnit
            },
            downtime: {
                total_minutes: totalDowntime,
                planned_minutes: plannedDowntime,
                unplanned_minutes: unplannedDowntime
            },
            maintenance: {
                mtbf_hours: 0, // TODO: Calculate properly
                mttr_minutes: 0 // TODO: Calculate properly
            },
            targets: {
                oee: avgTargetOee,
                availability: avgTargetAvailability,
                performance: avgTargetPerformance,
                quality: avgTargetQuality
            }
        };
    }

    /**
     * Get machine events for a specific asset (or list of assets) and time range
     */
    getMachineEvents(
        assetIds: string | string[],
        startTime: string,
        endTime: string,
        eventType?: string,
        limit: number = 20,
        offset: number = 0,
        includeArchived: boolean = false
    ): Observable<EventLogResponse> {
        let params = new HttpParams()
            .set('limit', limit.toString())
            .set('offset', offset.toString());

        if (startTime) {
            params = params.set('start_time', startTime);
        }
        if (endTime) {
            params = params.set('end_time', endTime);
        }

        if (eventType) {
            params = params.set('event_type', eventType);
        }

        if (includeArchived) {
            params = params.set('include_archived', 'true');
        }

        const id = Array.isArray(assetIds) ? assetIds[0] : assetIds;
        return this.http.get<EventLogResponse>(`${environment.api}/oee/events/${id}`, { params });
    }

    /**
     * Get state timeline segments for visualization
     */
    getStateTimeline(
        assetIds: string | string[],
        startTime: string,
        endTime: string,
        filteredEventTypes?: Set<string>
    ): Observable<StateTimelineSegment[]> {
        // Fetch events and build timeline locally
        const ids = Array.isArray(assetIds) ? assetIds : [assetIds];

        // Fetch events for all assets (limit 1000 to get all events for the day)
        const eventObservables = ids.map(id =>
            this.getMachineEvents(id, startTime, endTime, undefined, 1000, 0).pipe(
                map(response => response.events)
            )
        );

        return forkJoin(eventObservables).pipe(
            map(eventsArrays => {
                // Flatten events array
                const allEvents = eventsArrays.reduce((acc, val) => acc.concat(val), []);

                let allSegments: StateTimelineSegment[] = [];
                ids.forEach(id => {
                    const segments = this.buildTimelineFromEvents(id, startTime, endTime, allEvents, filteredEventTypes);
                    allSegments = allSegments.concat(segments);
                });

                // Sort by start time
                allSegments.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

                return allSegments;
            })
        );
    }

    /**
     * Build state timeline from events
     * Derives state segments from time-bound events (downtime/production)
     * State is derived from event_type + downtime_reason_type + end_ts:
     * - production + no end_ts = RUNNING
     * - production + end_ts = COMPLETED
     * - downtime + planned reason = DOWN (blue)
     * - downtime + unplanned reason = DOWN (red)
     * - downtime + idle-category reason = IDLE (gray)
     * Note: All downtime events require a reason code (enforced by backend)
     */
    private buildTimelineFromEvents(equipmentId: string, startTime: string, endTime: string, events: MachineEvent[], filteredEventTypes?: Set<string>): StateTimelineSegment[] {
        const start = new Date(startTime).getTime();
        const end = new Date(endTime).getTime();

        // Get all time-bound events (run/batch and downtime) in the range FOR THIS EQUIPMENT
        const relevantEvents = events.filter(e => {
            // Check if event belongs to this equipment (either as primary or linked)
            const isForThisEquipment = e.equipment_id === equipmentId ||
                (e.linked_equipment_ids && e.linked_equipment_ids.includes(equipmentId));
            if (!isForThisEquipment) return false;

            if (e.type !== 'production' && e.type !== 'batch' && e.type !== 'downtime') return false;

            const eventStart = e.start_ts ? new Date(e.start_ts).getTime() : 0;
            const eventEnd = e.end_ts ? new Date(e.end_ts).getTime() : Date.now();

            // Include if event overlaps with time range
            return eventEnd >= start && eventStart <= end;
        }).sort((a, b) => {
            const aStart = new Date(a.start_ts || a.created_at).getTime();
            const bStart = new Date(b.start_ts || b.created_at).getTime();
            return aStart - bStart;
        });

        // Build timeline segments from events
        const segments: StateTimelineSegment[] = [];

        relevantEvents.forEach(event => {
            const eventStart = event.start_ts ? new Date(event.start_ts) : new Date(event.created_at);
            const eventEnd = event.end_ts ? new Date(event.end_ts) : undefined;

            const duration = eventEnd
                ? (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60)
                : undefined;

            // Determine state based on event type and reason code
            let state: MachineState;
            let downtime_reason_code: string | undefined;
            let downtime_reason_display: string | undefined;
            let downtime_reason_type: DowntimeReasonType | undefined;

            if (event.type === 'production' || event.type === 'batch') {
                // Show "COMPLETED" for completed production/batch, "RUNNING" for ongoing
                state = eventEnd ? 'COMPLETED' : 'RUNNING';
            } else if (event.type === 'downtime') {
                // All downtime events now require a reason code (enforced by backend)
                // The state is always DOWN; the reason's group type determines display
                state = 'DOWN';
                downtime_reason_code = event.downtime_reason?.code;
                downtime_reason_display = event.downtime_reason?.display_name;
                // Use API-provided type (the API always provides this for downtime events)
                downtime_reason_type = event.downtime_reason_type;
            } else {
                return; // Skip other event types
            }

            // Determine color based on state and downtime_reason_type
            // Unified color scheme:
            // - Green (#10B981/#059669): Running/Completed
            // - Red (#EF4444): Unplanned downtime
            // - Blue (#3B82F6): Planned downtime
            // - Gray (#9CA3AF): Idle-category downtime (has a reason from an 'idle' type group)
            let color = this.getStateColor(state);
            if (state === 'DOWN' && downtime_reason_type) {
                if (downtime_reason_type === 'planned') {
                    color = '#3B82F6'; // Blue for planned
                } else if (downtime_reason_type === 'unplanned') {
                    color = '#EF4444'; // Red for unplanned
                } else if (downtime_reason_type === 'idle') {
                    color = '#9CA3AF'; // Gray for idle-category reasons
                }
            }

            const segment: StateTimelineSegment = {
                state,
                start: eventStart.toISOString(),
                end: eventEnd?.toISOString(),
                duration_minutes: duration,
                color,
                downtime_reason_code,
                downtime_reason_display,
                downtime_reason_type,
                is_synthesized: false,
                event_id: event.event_id
            };

            segments.push(segment);
        });

        // Return segments without synthesized gap-filling
        // Only real events from the database are shown on the timeline
        return segments;
    }

    /**
     * Fill gaps in the timeline with synthesized IDLE segments
     * This ensures the timeline is continuous from start to end
     */
    private fillTimelineGaps(segments: StateTimelineSegment[], rangeStart: number, rangeEnd: number): StateTimelineSegment[] {
        if (segments.length === 0) {
            // No events - entire range is IDLE
            const duration = (rangeEnd - rangeStart) / (1000 * 60);
            return [{
                state: 'IDLE',
                start: new Date(rangeStart).toISOString(),
                end: new Date(rangeEnd).toISOString(),
                duration_minutes: duration,
                color: this.getStateColor('IDLE'),
                is_synthesized: true
            }];
        }

        const result: StateTimelineSegment[] = [];

        // Sort segments by start time
        const sortedSegments = [...segments].sort((a, b) =>
            new Date(a.start).getTime() - new Date(b.start).getTime()
        );

        let currentTime = rangeStart;

        for (const segment of sortedSegments) {
            const segmentStart = new Date(segment.start).getTime();
            const segmentEnd = segment.end
                ? new Date(segment.end).getTime()
                : Math.min(Date.now(), rangeEnd);

            // If there's a gap before this segment, fill it with IDLE
            if (segmentStart > currentTime) {
                const gapDuration = (segmentStart - currentTime) / (1000 * 60);
                // Only create IDLE segment if gap is at least 1 minute
                if (gapDuration >= 1) {
                    result.push({
                        state: 'IDLE',
                        start: new Date(currentTime).toISOString(),
                        end: new Date(segmentStart).toISOString(),
                        duration_minutes: gapDuration,
                        color: this.getStateColor('IDLE'),
                        is_synthesized: true
                    });
                }
            }

            // Add the actual segment
            result.push(segment);

            // Update current time to end of this segment
            currentTime = Math.max(currentTime, segmentEnd);
        }

        // Fill any remaining gap at the end
        if (currentTime < rangeEnd) {
            const gapDuration = (rangeEnd - currentTime) / (1000 * 60);
            // Only create IDLE segment if gap is at least 1 minute
            if (gapDuration >= 1) {
                result.push({
                    state: 'IDLE',
                    start: new Date(currentTime).toISOString(),
                    end: new Date(rangeEnd).toISOString(),
                    duration_minutes: gapDuration,
                    color: this.getStateColor('IDLE'),
                    is_synthesized: true
                });
            }
        }

        return result;
    }

    /**
     * Get color for machine state
     * Unified color scheme:
     * - Green: Running/production
     * - Red: Unplanned downtime (default for DOWN)
     * - Blue: Planned downtime
     * - Gray: Idle/synthesized gaps
     */
    private getStateColor(state: string): string {
        const MACHINE_STATE_COLORS: { [key: string]: string } = {
            'RUNNING': '#10B981',      // Green - ongoing run
            'COMPLETED': '#059669',    // Darker green - completed run
            'DOWN': '#EF4444',         // Red - unplanned downtime (default)
            'IDLE': '#9CA3AF',         // Gray - idle/synthesized
            'MAINTENANCE': '#3B82F6',  // Blue - planned maintenance
            'SCHEDULED': '#F59E0B',    // Orange/amber - scheduled batch
        };
        return MACHINE_STATE_COLORS[state] || '#9CA3AF'; // Default to gray
    }

    /**
     * Get list of available downtime reasons
     * Note: type is inherited from the parent downtime reason group, not stored on the reason itself.
     * To filter by type, use getDowntimeReasonGroups and filter by group type.
     */
    getDowntimeReasons(groupId?: string, siteId?: string, stationId?: string): Observable<DowntimeReason[]> {
        const resolvedSiteId = siteId || this.getSelectedSiteId();

        let params = new HttpParams();
        if (groupId) {
            params = params.set('group_id', groupId);
        }
        if (resolvedSiteId) {
            params = params.set('site_id', resolvedSiteId);
        }
        if (stationId) {
            params = params.set('station_id', stationId);
        }
        return this.http.get<{ id: string; site_id: string; code: string; name: string; description: string; is_active: boolean; group_id: string; enabled_station_ids?: string[] }[]>(`${environment.api}/oee/downtime-reasons`, { params }).pipe(
            map(rows => rows.map(row => ({
                reason_id: row.id,
                site_id: resolvedSiteId || row.site_id,
                code: row.code,
                display_name: row.name,
                description: row.description,
                color_hex: '#cccccc', // Default
                is_active: row.is_active,
                group_id: row.group_id, // Reference to parent group
                enabled_station_ids: row.enabled_station_ids || [],
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            })))
        );
    }

    /**
     * Get list of downtime reason groups for a site
     * Groups define the type (planned/unplanned/idle) that all their downtime reasons inherit
     */
    getDowntimeReasonGroups(siteId?: string): Observable<DowntimeReasonGroup[]> {
        const resolvedSiteId = siteId || this.getSelectedSiteId();

        let params = new HttpParams();
        if (resolvedSiteId) {
            params = params.set('site_id', resolvedSiteId);
        }
        return this.http.get<{ id: string; name: string; type: DowntimeReasonType; sort_order: number }[]>(`${environment.api}/oee/downtime-reason-groups`, { params }).pipe(
            map(rows => rows.map(row => ({
                group_id: row.id,
                site_id: resolvedSiteId || '',
                name: row.name,
                type: row.type,
                sort_order: row.sort_order
            })))
        );
    }

    /**
     * Create a new downtime reason group
     */
    createDowntimeReasonGroup(group: { name: string; type: DowntimeReasonType; site_id: string }): Observable<DowntimeReasonGroup> {
        return this.http.post<{ group_id: string; name: string; type: DowntimeReasonType }>(`${environment.api}/oee/downtime-reason-group`, {
            name: group.name,
            type: group.type,
            site_id: group.site_id
        }).pipe(
            map(response => ({
                group_id: response.group_id,
                site_id: group.site_id,
                name: response.name,  // Use server-generated unique name
                type: response.type,
                sort_order: 0
            }))
        );
    }

    /**
     * Update a downtime reason group (name and/or type)
     */
    updateDowntimeReasonGroup(groupId: string, updates: { name?: string; type?: DowntimeReasonType }): Observable<void> {
        return this.http.put<void>(`${environment.api}/oee/downtime-reason-group/${groupId}`, updates);
    }

    /**
     * Delete a downtime reason group (cascades to delete all reasons in the group)
     */
    deleteDowntimeReasonGroup(groupId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/oee/downtime-reason-group/${groupId}`);
    }

    /**
     * Add a new downtime reason
     * Note: type is passed to create the parent group if it doesn't exist,
     * but is NOT stored on the reason itself
     */
    addDowntimeReason(reason: DowntimeReason): Observable<DowntimeReason> {
        return this.http.post<{ id: string }>(`${environment.api}/oee/downtime-reason`, {
            code: reason.code,
            name: reason.display_name,
            description: reason.description,
            group_id: reason.group_id,
            site_id: reason.site_id,
            enabled_station_ids: reason.enabled_station_ids
        }).pipe(
            map(response => ({
                ...reason,
                reason_id: response.id
            }))
        );
    }

    /**
     * Update an existing downtime reason
     */
    updateDowntimeReason(reason: DowntimeReason): Observable<DowntimeReason> {
        return this.http.put<DowntimeReason>(`${environment.api}/oee/downtime-reason/${reason.reason_id}`, {
            code: reason.code,
            name: reason.display_name,
            description: reason.description,
            enabled_station_ids: reason.enabled_station_ids
        });
    }

    /**
     * Delete a downtime reason
     */
    deleteDowntimeReason(id: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/oee/downtime-reason/${id}`);
    }

    /**
     * Get the usage count for a downtime reason (how many events use it)
     */
    getDowntimeReasonUsageCount(id: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/oee/downtime-reason/${id}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    /**
     * Reassign all events from one downtime reason to another, then delete the old reason
     * Returns the number of events that were reassigned
     */
    reassignAndDeleteDowntimeReason(oldReasonId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/oee/downtime-reason/${oldReasonId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    /**
     * Get the usage count for a downtime reason group (how many events use any reason in the group)
     */
    getDowntimeGroupUsageCount(groupId: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/oee/downtime-reason-group/${groupId}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    /**
     * Reassign all events from reasons in a group to a new reason, then delete the group
     * Returns the number of events that were reassigned
     */
    reassignAndDeleteDowntimeGroup(groupId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/oee/downtime-reason-group/${groupId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    // ============================================
    // Incident Reason Methods
    // ============================================

    /**
     * Get list of incident reasons for a site
     */
    getIncidentReasons(siteId?: string): Observable<IncidentReason[]> {
        const resolvedSiteId = siteId || this.getSelectedSiteId();

        let params = new HttpParams();
        if (resolvedSiteId) {
            params = params.set('site_id', resolvedSiteId);
        }
        return this.http.get<{ id: string; code: string; name: string; description: string; group_id: string; group_name: string; is_active: boolean; sort_order: number }[]>(
            `${environment.api}/oee/incident-reasons`, { params }
        ).pipe(
            map(rows => rows.map(row => ({
                id: row.id,
                site_id: resolvedSiteId,
                code: row.code,
                name: row.name,
                description: row.description,
                is_active: row.is_active,
                sort_order: row.sort_order,
                group_id: row.group_id,
                group_name: row.group_name
            })))
        );
    }

    /**
     * Get list of incident reason groups for a site
     */
    getIncidentReasonGroups(siteId?: string): Observable<IncidentReasonGroup[]> {
        const resolvedSiteId = siteId || this.getSelectedSiteId();

        let params = new HttpParams();
        if (resolvedSiteId) {
            params = params.set('site_id', resolvedSiteId);
        }

        interface GroupResponse {
            id: string;
            name: string;
            sort_order: number;
            reasons?: Array<{
                id: string;
                code: string;
                name: string;
                description?: string;
                group_id: string;
                group_name?: string;
                is_active: boolean;
                sort_order: number;
            }>;
        }

        return this.http.get<GroupResponse[]>(
            `${environment.api}/oee/incident-reason-groups`, { params }
        ).pipe(
            map(rows => rows.map(row => ({
                id: row.id,
                site_id: resolvedSiteId,
                name: row.name,
                sort_order: row.sort_order,
                reasons: row.reasons?.map(r => ({
                    id: r.id,
                    code: r.code,
                    name: r.name,
                    description: r.description,
                    group_id: r.group_id,
                    group_name: r.group_name,
                    is_active: r.is_active,
                    sort_order: r.sort_order
                }))
            })))
        );
    }

    /**
     * Create a new incident reason group
     */
    createIncidentReasonGroup(group: { name: string; site_id: string }): Observable<IncidentReasonGroup> {
        return this.http.post<{ group_id: string; name: string }>(
            `${environment.api}/oee/incident-reason-group`,
            { name: group.name, site_id: group.site_id }
        ).pipe(
            map(response => ({
                id: response.group_id,
                site_id: group.site_id,
                name: response.name,
                sort_order: 0
            }))
        );
    }

    /**
     * Update an incident reason group
     */
    updateIncidentReasonGroup(groupId: string, updates: { name?: string }): Observable<void> {
        return this.http.put<void>(`${environment.api}/oee/incident-reason-group/${groupId}`, updates);
    }

    /**
     * Delete an incident reason group (cascades to delete all reasons)
     */
    deleteIncidentReasonGroup(groupId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/oee/incident-reason-group/${groupId}`);
    }

    /**
     * Create a new incident reason
     */
    createIncidentReason(reason: { code: string; name: string; description?: string; group_id: string; site_id: string }): Observable<IncidentReason> {
        return this.http.post<{ id: string }>(`${environment.api}/oee/incident-reason`, reason).pipe(
            map(response => ({
                id: response.id,
                code: reason.code,
                name: reason.name,
                description: reason.description,
                group_id: reason.group_id,
                is_active: true,
                sort_order: 0
            }))
        );
    }

    /**
     * Update an incident reason
     */
    updateIncidentReason(reasonId: string, updates: { code?: string; name?: string; description?: string }): Observable<void> {
        return this.http.put<void>(`${environment.api}/oee/incident-reason/${reasonId}`, updates);
    }

    /**
     * Delete an incident reason
     */
    deleteIncidentReason(reasonId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/oee/incident-reason/${reasonId}`);
    }

    /**
     * Get the usage count for an incident reason (how many events use it)
     */
    getIncidentReasonUsageCount(reasonId: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/oee/incident-reason/${reasonId}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    /**
     * Reassign all events from one incident reason to another, then delete the old reason
     * Returns the number of events that were reassigned
     */
    reassignAndDeleteIncidentReason(oldReasonId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/oee/incident-reason/${oldReasonId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    /**
     * Get the usage count for an incident reason group (how many events use any reason in the group)
     */
    getIncidentGroupUsageCount(groupId: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/oee/incident-reason-group/${groupId}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    /**
     * Reassign all events from reasons in a group to a new reason, then delete the group
     * Returns the number of events that were reassigned
     */
    reassignAndDeleteIncidentGroup(groupId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/oee/incident-reason-group/${groupId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    /**
     * Log a new incident event
     */
    logIncidentEvent(request: {
        equipment_ids: string[];
        primary_equipment_id: string;
        incident_reason_id: string;
        notes?: string;
        start_ts?: string;
        end_ts?: string;
    }): Observable<{ event_id: string; message: string }> {
        return this.http.post<{ event_id: string; message: string }>(`${environment.api}/oee/incident`, request);
    }

    /**
     * Log a new station-level incident event
     */
    logStationIncidentEvent(request: {
        station_id: string;
        incident_reason_id: string;
        notes?: string;
        start_ts?: string;
        end_ts?: string;
    }): Observable<{ event_id: string; message: string }> {
        return this.http.post<{ event_id: string; message: string }>(
            `${environment.api}/ops/station/${request.station_id}/incident`, request
        );
    }

    /**
     * Get station incident reason groups (with nested reasons) for a site
     */
    getStationIncidentReasonGroups(siteId?: string): Observable<IncidentReasonGroup[]> {
        const resolvedSiteId = siteId || this.getSelectedSiteId();

        let params = new HttpParams();
        if (resolvedSiteId) {
            params = params.set('site_id', resolvedSiteId);
        }

        interface GroupResponse {
            id: string;
            name: string;
            sort_order: number;
            reasons?: Array<{
                id: string;
                code: string;
                name: string;
                description?: string;
                group_id: string;
                group_name?: string;
                is_active: boolean;
                sort_order: number;
            }>;
        }

        return this.http.get<GroupResponse[]>(
            `${environment.api}/ops/station-incident-reason-groups`, { params }
        ).pipe(
            map(groups => groups.map(g => ({
                id: g.id,
                name: g.name,
                sort_order: g.sort_order,
                reasons: g.reasons?.map(r => ({
                    id: r.id,
                    code: r.code,
                    name: r.name,
                    description: r.description,
                    is_active: r.is_active,
                    sort_order: r.sort_order,
                    group_id: r.group_id,
                    group_name: r.group_name
                })) || []
            })))
        );
    }

    // ============================================
    // Station Incident Reason CRUD Functions
    // ============================================

    createStationIncidentReasonGroup(group: { name: string; site_id: string }): Observable<IncidentReasonGroup> {
        return this.http.post<{ group_id: string; name: string }>(
            `${environment.api}/ops/station-incident-reason-group`,
            { name: group.name, site_id: group.site_id }
        ).pipe(
            map(response => ({
                id: response.group_id,
                site_id: group.site_id,
                name: response.name,
                sort_order: 0
            }))
        );
    }

    updateStationIncidentReasonGroup(groupId: string, updates: { name?: string }): Observable<void> {
        return this.http.put<void>(`${environment.api}/ops/station-incident-reason-group/${groupId}`, updates);
    }

    deleteStationIncidentReasonGroup(groupId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/ops/station-incident-reason-group/${groupId}`);
    }

    createStationIncidentReason(reason: { code: string; name: string; description?: string; group_id: string; site_id: string }): Observable<IncidentReason> {
        return this.http.post<{ id: string }>(`${environment.api}/ops/station-incident-reason`, reason).pipe(
            map(response => ({
                id: response.id,
                code: reason.code,
                name: reason.name,
                description: reason.description,
                group_id: reason.group_id,
                is_active: true,
                sort_order: 0
            }))
        );
    }

    updateStationIncidentReason(reasonId: string, updates: { code?: string; name?: string; description?: string }): Observable<void> {
        return this.http.put<void>(`${environment.api}/ops/station-incident-reason/${reasonId}`, updates);
    }

    deleteStationIncidentReason(reasonId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/ops/station-incident-reason/${reasonId}`);
    }

    getStationIncidentReasonUsageCount(reasonId: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/ops/station-incident-reason/${reasonId}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    reassignAndDeleteStationIncidentReason(oldReasonId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/ops/station-incident-reason/${oldReasonId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    getStationIncidentGroupUsageCount(groupId: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/ops/station-incident-reason-group/${groupId}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    reassignAndDeleteStationIncidentGroup(groupId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/ops/station-incident-reason-group/${groupId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    // ============================================
    // Abort Reason Functions (Issue #2677)
    // ============================================

    /**
     * Get list of abort reasons for a site
     */
    getAbortReasons(siteId?: string): Observable<AbortReason[]> {
        const resolvedSiteId = siteId || this.getSelectedSiteId();

        let params = new HttpParams();
        if (resolvedSiteId) {
            params = params.set('site_id', resolvedSiteId);
        }
        return this.http.get<{ id: string; code: string; name: string; description: string; group_id: string; group_name: string; is_active: boolean }[]>(
            `${environment.api}/oee/abort-reasons`, { params }
        ).pipe(
            map(rows => rows.map(row => ({
                id: row.id,
                site_id: resolvedSiteId,
                code: row.code,
                name: row.name,
                description: row.description,
                group_id: row.group_id,
                group_name: row.group_name,
                is_active: row.is_active
            })))
        );
    }

    /**
     * Get list of abort reason groups for a site (with nested reasons)
     */
    getAbortReasonGroups(siteId?: string): Observable<AbortReasonGroup[]> {
        const resolvedSiteId = siteId || this.getSelectedSiteId();

        let params = new HttpParams();
        if (resolvedSiteId) {
            params = params.set('site_id', resolvedSiteId);
        }

        interface GroupResponse {
            id: string;
            name: string;
            sort_order: number;
            reasons?: Array<{
                id: string;
                code: string;
                name: string;
                description?: string;
                group_id: string;
                group_name: string;
                is_active: boolean;
            }>;
        }

        return this.http.get<GroupResponse[]>(
            `${environment.api}/oee/abort-reason-groups`, { params }
        ).pipe(
            map(groups => groups.map(g => ({
                id: g.id,
                site_id: resolvedSiteId,
                name: g.name,
                sort_order: g.sort_order,
                reasons: g.reasons?.map(r => ({
                    id: r.id,
                    code: r.code,
                    name: r.name,
                    description: r.description,
                    group_id: r.group_id,
                    group_name: r.group_name,
                    is_active: r.is_active
                }))
            })))
        );
    }

    /**
     * Create a new abort reason group
     */
    createAbortReasonGroup(group: { name: string; site_id: string }): Observable<AbortReasonGroup> {
        return this.http.post<{ group_id: string; name: string }>(
            `${environment.api}/oee/abort-reason-group`,
            { name: group.name, site_id: group.site_id }
        ).pipe(
            map(response => ({
                id: response.group_id,
                site_id: group.site_id,
                name: response.name,
                sort_order: 0
            }))
        );
    }

    /**
     * Update an abort reason group
     */
    updateAbortReasonGroup(groupId: string, updates: { name?: string }): Observable<void> {
        return this.http.put<void>(`${environment.api}/oee/abort-reason-group/${groupId}`, updates);
    }

    /**
     * Delete an abort reason group (cascades to delete all reasons)
     */
    deleteAbortReasonGroup(groupId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/oee/abort-reason-group/${groupId}`);
    }

    /**
     * Create a new abort reason
     */
    createAbortReason(reason: { code: string; name: string; description?: string; group_id: string; site_id: string }): Observable<AbortReason> {
        return this.http.post<{ id: string }>(`${environment.api}/oee/abort-reason`, reason).pipe(
            map(response => ({
                id: response.id,
                code: reason.code,
                name: reason.name,
                description: reason.description,
                group_id: reason.group_id,
                is_active: true
            }))
        );
    }

    /**
     * Update an abort reason
     */
    updateAbortReason(reasonId: string, updates: { code?: string; name?: string; description?: string; is_active?: boolean }): Observable<void> {
        return this.http.put<void>(`${environment.api}/oee/abort-reason/${reasonId}`, updates);
    }

    /**
     * Delete an abort reason
     */
    deleteAbortReason(reasonId: string): Observable<void> {
        return this.http.delete<void>(`${environment.api}/oee/abort-reason/${reasonId}`);
    }

    /**
     * Get the usage count for an abort reason (how many batches use it)
     */
    getAbortReasonUsageCount(reasonId: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/oee/abort-reason/${reasonId}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    /**
     * Reassign all batches from one abort reason to another, then delete the old reason
     * Returns the number of batches that were reassigned
     */
    reassignAndDeleteAbortReason(oldReasonId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/oee/abort-reason/${oldReasonId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    /**
     * Get the usage count for an abort reason group (how many batches use any reason in the group)
     */
    getAbortGroupUsageCount(groupId: string): Observable<number> {
        return this.http.get<{ usageCount: number }>(`${environment.api}/oee/abort-reason-group/${groupId}/usage-count`)
            .pipe(map(res => res.usageCount));
    }

    /**
     * Reassign all batches from reasons in a group to a new reason, then delete the group
     * Returns the number of batches that were reassigned
     */
    reassignAndDeleteAbortGroup(groupId: string, newReasonId: string): Observable<number> {
        return this.http.delete<{ reassignedCount: number }>(`${environment.api}/oee/abort-reason-group/${groupId}/reassign-delete`, {
            body: { newReasonId }
        }).pipe(map(res => res.reassignedCount));
    }

    /**
     * Abort a running batch (Issue #2677)
     * Requires an abort reason - aborted batches are excluded from Quality calculation
     */
    abortBatch(request: AbortBatchRequest): Observable<AbortBatchResponse> {
        return this.http.post<AbortBatchResponse>(`${environment.api}/oee/batch/abort`, request);
    }

    /**
     * Resume (unabort) an aborted batch (Issue #2677)
     * Sets the batch back to running status and clears the abort reason
     */
    unabortBatch(request: UnabortBatchRequest): Observable<UnabortBatchResponse> {
        return this.http.post<UnabortBatchResponse>(`${environment.api}/oee/batch/unabort`, request);
    }

    /**
     * Mark a batch ready for review (canonical lifecycle: idle -> ready_for_review).
     */
    markBatchReadyForReview(request: MarkReadyForReviewRequest): Observable<BatchLifecycleResponse> {
        return this.http.post<BatchLifecycleResponse>(`${environment.api}/oee/batch/mark-ready-for-review`, request);
    }

    /**
     * Approve a batch that is ready for review (canonical lifecycle: ready_for_review -> approved).
     */
    markBatchApproved(request: MarkApprovedRequest): Observable<BatchLifecycleResponse> {
        return this.http.post<BatchLifecycleResponse>(`${environment.api}/oee/batch/mark-approved`, request);
    }

    /**
     * Log a new machine event
     */
    logEvent(event: CreateEventRequest): Observable<{ event_id: string; message: string }> {
        return this.http.post<{ event_id: string; message: string }>(`${environment.api}/oee/event`, event);
    }

    /**
     * Log a multi-equipment event (links one event to multiple pieces of equipment)
     */
    logMultiEquipmentEvent(event: {
        equipment_ids: string[];
        primary_equipment_id: string;
        type: string;
        start_ts: string;
        end_ts?: string;
        downtime_reason_id?: string;
        notes?: string;
        troubleshooting_notes?: string;
        resolution_notes?: string;
    }): Observable<{ event_id: string; message: string; equipment_count: number }> {
        return this.http.post<{ event_id: string; message: string; equipment_count: number }>(
            `${environment.api}/oee/event/multi-equipment`,
            event
        );
    }

    /**
     * Get asset information
     */
    getAssetInfo(assetId: string): Observable<Record<string, unknown>> {
        // TODO: Replace with real HTTP call when endpoint is available
        // return this.http.get(`${environment.api}/oee/asset/${assetId}`);
        return of({ asset_id: assetId, asset_name: 'Equipment' });
    }

    /**
     * Calculate time range dates based on preset options
     * Uses the browser's local timezone for calendar-based options
     */
    getTimeRangeDates(option: string): { start: Date; end: Date } {
        const now = new Date();
        let start: Date;
        let end: Date;

        // Get current date components in local time
        const year = now.getFullYear();
        const month = now.getMonth(); // 0-11
        const day = now.getDate();
        const dayOfWeek = now.getDay(); // 0 = Sunday

        switch (option) {
            case 'last_hour':
                end = new Date(now);
                start = new Date(now.getTime() - (60 * 60 * 1000));
                break;

            case 'last_24_hours':
                end = new Date(now);
                start = new Date(now.getTime() - (24 * 60 * 60 * 1000));
                break;

            case 'today':
                // Today from midnight to end of day
                start = new Date(year, month, day, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'yesterday':
                start = new Date(year, month, day - 1, 0, 0, 0, 0);
                end = new Date(year, month, day - 1, 23, 59, 59, 999);
                break;

            case 'last_7_days':
                start = new Date(year, month, day - 6, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'last_30_days':
                start = new Date(year, month, day - 29, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'last_3_months':
                start = new Date(year, month - 3, day, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'last_1_year':
                start = new Date(year - 1, month, day, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'max':
                // From 10 years ago to now (effectively all data)
                start = new Date(year - 10, 0, 1, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'this_week':
                // Current week: Sunday to Saturday (or today if not yet Saturday)
                const thisSunday = new Date(year, month, day - dayOfWeek, 0, 0, 0, 0);
                start = thisSunday;
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'last_week':
                // Previous Sunday to Saturday
                const lastSunday = new Date(year, month, day - dayOfWeek - 7, 0, 0, 0, 0);
                const lastSaturday = new Date(year, month, day - dayOfWeek - 1, 23, 59, 59, 999);
                start = lastSunday;
                end = lastSaturday;
                break;

            case 'this_month':
                // Current month: 1st to today
                start = new Date(year, month, 1, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
                break;

            case 'last_month':
                const lastMonthStart = new Date(year, month - 1, 1, 0, 0, 0, 0);
                const lastMonthEnd = new Date(year, month, 0, 23, 59, 59, 999); // Day 0 = last day of prev month
                start = lastMonthStart;
                end = lastMonthEnd;
                break;

            default:
                // Default to today
                start = new Date(year, month, day, 0, 0, 0, 0);
                end = new Date(year, month, day, 23, 59, 59, 999);
        }

        return { start, end };
    }

    /**
     * Format date to ISO string for API calls
     */
    toISOString(date: Date): string {
        return date.toISOString();
    }

    // ===================================================================
    // Load/Batch Tracking Methods (Phase 1A: Batch-Based Sterilization)
    // ===================================================================

    /**
     * Get loads for a specific equipment and time range
     */
    getBatches(equipmentId: string | string[], startDate: string, endDate: string): Observable<Batch[]> {
        const params = new HttpParams()
            .set('start', startDate)
            .set('end', endDate);

        const id = Array.isArray(equipmentId) ? equipmentId[0] : equipmentId;
        return this.http.get<Batch[]>(`${environment.api}/oee/batches/${id}`, { params });
    }

    /**
     * Get batches associated with a production event
     * In the refactored model, production events can have multiple batches
     */
    getBatchesForProductionEvent(eventId: string): Observable<Batch[]> {
        return this.http.get<Batch[]>(`${environment.api}/oee/events/${eventId}/batches`);
    }

    /**
     * Get loads across all equipment for a time range
     * Useful for building autocomplete lists of part numbers and work orders
     */
    getAllBatches(startDate: string, endDate: string): Observable<Batch[]> {
        const params = new HttpParams()
            .set('start', startDate)
            .set('end', endDate);

        return this.http.get<Batch[]>(`${environment.api}/oee/batches/all`, { params });
    }

    /**
     * Get unique batch names for autocomplete with context
     * Returns batch_number values with last equipment and timestamp
     */
    getAllBatchNames(startDate: string, endDate: string): Observable<BatchNameAutocompleteItem[]> {
        const params = new HttpParams()
            .set('start', startDate)
            .set('end', endDate);

        return this.http.get<BatchNameAutocompleteItem[]>(`${environment.api}/oee/batch-names`, { params });
    }

    /**
     * Update a batch's batch_number
     */
    updateBatchNumber(batchId: string, batchNumber: string): Observable<void> {
        return this.http.patch<void>(`${environment.api}/oee/batch/${batchId}/number`, { batch_number: batchNumber });
    }

    /**
     * Update a completed batch's details (batch_number, good_parts, reject_parts, start_ts, end_ts, custom_field_values)
     * Use clear_end_ts: true to clear the end date and return batch to in-progress state
     */
    updateBatch(request: { batch_id: string; batch_number?: string; good_parts?: number; reject_parts?: number; start_ts?: string; end_ts?: string; clear_end_ts?: boolean; product_id?: string; custom_field_values?: Record<string, unknown> }): Observable<Batch> {
        return this.http.patch<Batch>(`${environment.api}/oee/batch/${request.batch_id}`, request);
    }

    /**
     * Get current in-progress load for equipment
     */
    getCurrentBatch(equipmentId: string): Observable<Batch | null> {
        return this.http.get<Batch | null>(`${environment.api}/oee/batch/current/${equipmentId}`);
    }

    /**
     * Start a new load/batch
     */
    startBatch(request: CreateBatchRequest): Observable<Batch> {
        return this.http.post<Batch>(`${environment.api}/oee/batch/start`, request);
    }

    /** Cached next-batch-number preview per site so the simplified Start modal can render instantly. */
    private nextBatchNumberCache = new Map<string, string>();

    /**
     * Preview the next auto-generated batch number for a site, without creating it.
     * Used by the simplified Start Job modal to pre-fill the Job Number field.
     * Caches the last result per site so repeat opens are instant.
     */
    previewNextBatchNumber(siteId: string): Observable<{ batch_number: string }> {
        const params = new HttpParams().set('site_id', siteId);
        return this.http.get<{ batch_number: string }>(`${environment.api}/oee/batch/preview-number`, { params })
            .pipe(tap(({ batch_number }) => {
                if (batch_number) this.nextBatchNumberCache.set(siteId, batch_number);
            }));
    }

    /** Synchronously return the last previewed batch number for a site, or '' if not fetched yet. */
    getCachedNextBatchNumber(siteId: string): string {
        return this.nextBatchNumberCache.get(siteId) ?? '';
    }

    /** Invalidate the cached preview after a batch is actually started — forces next open to refetch. */
    invalidateNextBatchNumberCache(siteId?: string): void {
        if (siteId) {
            this.nextBatchNumberCache.delete(siteId);
        } else {
            this.nextBatchNumberCache.clear();
        }
    }

    /**
     * Complete an existing load
     */
    completeBatch(request: CompleteBatchRequest): Observable<Batch> {
        return this.http.post<Batch>(`${environment.api}/oee/batch/complete`, request);
    }

    /**
     * Cancel a load/batch
     */
    cancelBatch(batchId: string, reason: string): Observable<Batch> {
        return this.http.put<Batch>(`${environment.api}/oee/batch/${batchId}/cancel`, { reason });
    }

    // ==================== MULTI-BATCH PRODUCTION ====================

    /**
     * Get all current running batches for equipment (for multi-batch equipment)
     */
    getCurrentBatches(equipmentId: string): Observable<Batch[]> {
        return this.http.get<Batch[]>(`${environment.api}/oee/batches/current/${equipmentId}`);
    }

    /**
     * Get load by ID
     */
    getBatch(batchId: string): Observable<Batch | null> {
        return this.http.get<Batch | null>(`${environment.api}/oee/batch/${batchId}`);
    }

    // ==================== SYSTEMS & EQUIPMENT ====================

    /**
     * Get all sites
     */
    getSites(): Observable<Site[]> {
        return this.http.get<Site[]>(`${environment.api}/oee/sites`);
    }

    /**
     * Create a new site
     */
    createSite(site: { code: string, name: string, description?: string }): Observable<Site> {
        return this.http.post<Site>(`${environment.api}/oee/site`, site);
    }

    /**
     * Update an existing site
     */
    updateSite(siteId: string, site: { code: string, name: string, description?: string }): Observable<Site> {
        return this.http.put<Site>(`${environment.api}/oee/site/${siteId}`, site);
    }

    /**
     * Get all systems for a site
     */
    getSystems(siteId: string): Observable<System[]> {
        const params = new HttpParams().set('site_id', siteId);
        return this.http.get<System[]>(`${environment.api}/oee/systems`, { params });
    }

    /**
     * Create a new system
     */
    createSystem(system: System): Observable<System> {
        return this.http.post<{ id: string, response: string }>(`${environment.api}/oee/system`, {
            siteID: system.site_id,
            code: system.code,
            name: system.name,
            description: system.description
        }).pipe(
            map(response => ({
                ...system,
                id: response.id
            }))
        );
    }

    /**
     * Update an existing system
     */
    updateSystem(system: System): Observable<System> {
        return this.http.put<{ response: string }>(`${environment.api}/oee/system/${system.id}`, {
            code: system.code,
            name: system.name,
            description: system.description
        }).pipe(
            map(() => system)
        );
    }

    /**
     * Get all equipment for a site (optionally filtered by system)
     * Note: Does NOT calculate OEE metrics. Use getBatchOEEMetrics for that.
     * @param siteId The site ID to get equipment for
     * @param includeArchived Whether to include archived equipment (default: false)
     */
    getEquipment(siteId: string, includeArchived: boolean = false): Observable<Equipment[]> {
        let params = new HttpParams().set('site_id', siteId);
        if (includeArchived) {
            params = params.set('include_archived', 'true');
        }
        return this.http.get<Equipment[]>(`${environment.api}/oee/equipment`, { params });
    }

    /**
     * Persist the shared display order for equipment/stations on a site.
     */
    reorderEquipment(siteId: string, equipmentOrders: { id: string; order: number }[]): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(
            `${environment.api}/oee/equipment/reorder`,
            { siteId, equipmentOrders }
        );
    }

    /**
     * Get OEE metrics for a batch of equipment IDs for a specific time range
     * Returns a map of equipment ID -> OEEMetrics
     */
    getBatchOEEMetrics(
        assetIds: string[],
        startTime: string,
        endTime: string
    ): Observable<Map<string, OEEMetrics>> {
        // If no asset IDs provided, return empty map
        if (!assetIds || assetIds.length === 0) {
            return of(new Map<string, OEEMetrics>());
        }

        // Build request params
        const params = new HttpParams()
            .set('start_time', startTime)
            .set('end_time', endTime);

        // Create an observable for each equipment's metrics
        const requests: { [key: string]: Observable<OEEMetrics | null> } = {};
        assetIds.forEach(id => {
            requests[id] = this.http.get<OEEMetrics>(`${environment.api}/oee/metrics/${id}`, { params }).pipe(
                map(metrics => metrics || null)
            );
        });

        // Execute all requests in parallel and combine results
        return forkJoin(requests).pipe(
            map((results: { [key: string]: OEEMetrics | null }) => {
                const resultMap = new Map<string, OEEMetrics>();
                Object.entries(results).forEach(([id, metrics]) => {
                    if (metrics) {
                        resultMap.set(id, metrics);
                    }
                });
                return resultMap;
            })
        );
    }

    /**
     * Get equipment by ID
     */
    getEquipmentById(equipmentId: string): Observable<Equipment | null> {
        return this.http.get<Equipment | null>(`${environment.api}/oee/equipment/${equipmentId}`);
    }

    /**
     * Update equipment state
     * Called when events are created/updated to keep current_state in sync
     */
    updateEquipmentState(equipmentId: string, state: string, stateSince: string): Observable<Equipment> {
        return this.http.patch<Equipment>(`${environment.api}/oee/equipment/${equipmentId}/state`, { state, state_since: stateSince });
    }

    /**
     * Update the preferred display unit for product-derived target rates.
     */
    updateEquipmentIdealCycleTime(equipmentId: string, _idealCycleTimeSeconds: number, targetRateUnit?: RateUnit): Observable<Equipment> {
        if (targetRateUnit === undefined) {
            return this.getEquipmentById(equipmentId).pipe(
                map(equipment => equipment as Equipment)
            );
        }

        return this.http.patch<Equipment>(`${environment.api}/oee/equipment/${equipmentId}`, {
            targetRateUnit
        });
    }

    /**
     * Add new equipment
     */
    addEquipment(equipment: Equipment): Observable<Equipment> {
        return this.http.post<{ id: string, response: string }>(`${environment.api}/oee/equipment`, {
            siteID: equipment.site_id,
            systemID: equipment.system_id,
            code: equipment.code,
            name: equipment.name,
            equipmentNumber: equipment.equipment_number,
            description: equipment.description,
            targetRateUnit: equipment.target_rate_unit,
            target_oee_percent: equipment.target_oee_percent,
            target_availability_percent: equipment.target_availability_percent,
            target_performance_percent: equipment.target_performance_percent,
            target_quality_percent: equipment.target_quality_percent,
            dataCollectionStartDate: equipment.data_collection_start_date,
            startBatchText: equipment.start_batch_text,
            completeBatchText: equipment.complete_batch_text,
            targetConcurrentBatches: equipment.target_concurrent_batches ?? 1,
            allowsBatchOperators: equipment.allows_batch_operators ?? true,
            restrictBatchEquipment: equipment.restrict_batch_equipment ?? false,
            nodeType: equipment.node_type ?? 'equipment',
            machineOnly: equipment.node_type === 'station' ? !!equipment.machine_only : false,
            simplified_job_start: (equipment as any).simplified_job_start === true,
            simplified_job_complete: (equipment as any).simplified_job_complete === true,
            auto_resolve_previous_jobs: (equipment as any).auto_resolve_previous_jobs === true,
            allow_concurrent_jobs: (equipment as any).allow_concurrent_jobs === true
        }).pipe(
            map(response => ({
                ...equipment,
                id: response.id
            }))
        );
    }

    /**
     * Update existing equipment
     */
    updateEquipment(equipment: Equipment): Observable<Equipment> {
        const payload = {
            systemID: equipment.system_id,
            code: equipment.code,
            name: equipment.name,
            equipmentNumber: equipment.equipment_number,
            description: equipment.description,
            targetRateUnit: equipment.target_rate_unit,
            target_oee_percent: equipment.target_oee_percent,
            target_availability_percent: equipment.target_availability_percent,
            target_performance_percent: equipment.target_performance_percent,
            target_quality_percent: equipment.target_quality_percent,
            dataCollectionStartDate: equipment.data_collection_start_date,
            startBatchText: equipment.start_batch_text,
            completeBatchText: equipment.complete_batch_text,
            targetConcurrentBatches: equipment.target_concurrent_batches ?? 1,
            allowsBatchOperators: equipment.allows_batch_operators ?? true,
            restrictBatchEquipment: equipment.restrict_batch_equipment ?? false,
            nodeType: equipment.node_type ?? 'equipment',
            machineOnly: equipment.node_type === 'station' ? !!equipment.machine_only : false,
            simplified_job_start: (equipment as any).simplified_job_start === true,
            simplified_job_complete: (equipment as any).simplified_job_complete === true,
            auto_resolve_previous_jobs: (equipment as any).auto_resolve_previous_jobs === true,
            allow_concurrent_jobs: (equipment as any).allow_concurrent_jobs === true
        };

        return this.http.put<Equipment>(`${environment.api}/oee/equipment/${equipment.id}`, payload);
    }

    getCompatibleEquipment(equipmentId: string): Observable<{ compatible_equipment_id: string; name: string; code: string }[]> {
        return this.http.get<{ compatible_equipment_id: string; name: string; code: string }[]>(
            `${environment.api}/oee/equipment/${equipmentId}/compatible-equipment`
        );
    }

    setCompatibleEquipment(equipmentId: string, compatibleIds: string[]): Observable<any> {
        return this.http.put(
            `${environment.api}/oee/equipment/${equipmentId}/compatible-equipment`,
            { compatibleEquipmentIds: compatibleIds }
        );
    }

    // ============================================
    // Equipment Custom Fields
    // ============================================

    /**
     * Get all custom fields for an equipment (ordered by creation time)
     */
    getEquipmentCustomFields(equipmentId: string): Observable<EquipmentCustomField[]> {
        return this.http.get<EquipmentCustomField[]>(
            `${environment.api}/oee/equipment/${equipmentId}/custom-fields`
        );
    }

    /**
     * Create a new custom field for an equipment
     */
    createEquipmentCustomField(
        equipmentId: string,
        field: Omit<EquipmentCustomField, 'id' | 'equipment_id' | 'created_at'>
    ): Observable<EquipmentCustomField> {
        return this.http.post<EquipmentCustomField>(
            `${environment.api}/oee/equipment/${equipmentId}/custom-fields`,
            field
        );
    }

    /**
     * Update an existing custom field
     */
    updateEquipmentCustomField(
        equipmentId: string,
        fieldId: string,
        updates: Partial<Omit<EquipmentCustomField, 'id' | 'equipment_id' | 'created_at'>>
    ): Observable<EquipmentCustomField> {
        return this.http.put<EquipmentCustomField>(
            `${environment.api}/oee/equipment/${equipmentId}/custom-fields/${fieldId}`,
            updates
        );
    }

    /**
     * Delete a custom field (batch values preserve historical data)
     */
    deleteEquipmentCustomField(equipmentId: string, fieldId: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(
            `${environment.api}/oee/equipment/${equipmentId}/custom-fields/${fieldId}`
        );
    }

    /**
     * Get active (running) batches for a station
     */
    getActiveBatchesForStation(stationId: string): Observable<Batch[]> {
        return this.getCurrentBatches(stationId);
    }
    /**
     * Get active (running) batches for a station — enriched per #3078 with
     * touch_time_minutes, standard_hours (from product×station config), and
     * active_operator_count. Consumed by ActiveJobsListComponent.
     */
    getStationActiveJobs(stationId: string): Observable<StationActiveJob[]> {
        return this.http.get<StationActiveJob[]>(`${environment.api}/oee/stations/${stationId}/active-jobs`);
    }

    getStationCompletedJobs(stationId: string, from: Date, to: Date): Observable<StationCompletedJob[]> {
        const params = new URLSearchParams({
            from: from.toISOString(),
            to: to.toISOString(),
        }).toString()
        return this.http.get<StationCompletedJob[]>(`${environment.api}/oee/stations/${stationId}/completed-jobs?${params}`);
    }

    getStationCompletedJobCount(stationId: string, from: Date, to: Date): Observable<{ count: number }> {
        const params = new URLSearchParams({
            from: from.toISOString(),
            to: to.toISOString(),
        }).toString()
        return this.http.get<{ count: number }>(`${environment.api}/oee/stations/${stationId}/completed-job-count?${params}`);
    }

    /**
     * #3078 — Station-type KPI row aggregates over a date range.
     * `completedOnly=true` filters Earned/Actual hours to status='completed'.
     */
    getStationEfficiencyKpis(stationId: string, from: Date, to: Date, completedOnly: boolean): Observable<StationEfficiencyKpis> {
        const params = new URLSearchParams({
            from: from.toISOString(),
            to: to.toISOString(),
            completed_only: String(completedOnly),
        }).toString()
        return this.http.get<StationEfficiencyKpis>(`${environment.api}/oee/stations/${stationId}/kpis?${params}`);
    }

    getStationComparisonShiftUtilization(siteId: string, stationIds: string[], from: Date, to: Date): Observable<StationComparisonShiftUtilization> {
        const params = new URLSearchParams({
            station_ids: stationIds.join(','),
            from: from.toISOString(),
            to: to.toISOString(),
        }).toString()
        return this.http.get<StationComparisonShiftUtilization>(`${environment.api}/oee/sites/${siteId}/station-shift-utilization?${params}`);
    }




    /**
     * #3078 — Per-station worst-warning rollup for the Process Flow
     * canvas. Empty array when no station is breached. Stations not in
     * the response render with no icon (never default-yellow).
     */
    getProcessFlowStationWarnings(siteId: string): Observable<ProcessFlowStationWarning[]> {
        return this.http.get<ProcessFlowStationWarning[]>(
            `${environment.api}/oee/sites/${siteId}/process-flow-warnings`,
        );
    }

    /**
     * Get all custom fields for a station (ordered by creation time)
     */
    getStationCustomFields(stationId: string): Observable<StationCustomField[]> {
        return this.http.get<StationCustomField[]>(
            `${environment.api}/ops/station/${stationId}/custom-fields`
        );
    }

    /**
     * Get station custom fields by looking up the station for a given equipment ID
     */
    getStationCustomFieldsByEquipment(equipmentId: string): Observable<StationCustomField[]> {
        return this.http.get<StationCustomField[]>(
            `${environment.api}/oee/equipment/${equipmentId}/station-custom-fields`
        );
    }

    /**
     * Create a new custom field for a station
     */
    createStationCustomField(
        stationId: string,
        field: Omit<StationCustomField, 'id' | 'station_id' | 'created_at'>
    ): Observable<StationCustomField> {
        return this.http.post<StationCustomField>(
            `${environment.api}/ops/station/${stationId}/custom-fields`,
            field
        );
    }

    /**
     * Update an existing station custom field
     */
    updateStationCustomField(
        stationId: string,
        fieldId: string,
        updates: Partial<Omit<StationCustomField, 'id' | 'station_id' | 'created_at'>>
    ): Observable<StationCustomField> {
        return this.http.put<StationCustomField>(
            `${environment.api}/ops/station/${stationId}/custom-fields/${fieldId}`,
            updates
        );
    }

    /**
     * Delete a station custom field (batch values preserve historical data)
     */
    deleteStationCustomField(stationId: string, fieldId: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(
            `${environment.api}/ops/station/${stationId}/custom-fields/${fieldId}`
        );
    }

    // ============================================
    // Batch Custom Field Values
    // ============================================

    /**
     * Get custom field values for a batch on a specific event
     * Values are always retrieved in the context of an event
     */
    getBatchCustomFieldValues(batchId: string, eventId: string): Observable<BatchCustomFieldValue[]> {
        return this.http.get<BatchCustomFieldValue[]>(
            `${environment.api}/oee/batch/${batchId}/custom-fields`,
            { params: { eventId } }
        );
    }

    /**
     * Save custom field values for a batch on a specific event/equipment.
     * The station-named wrapper is kept for compatibility while station APIs are collapsed into equipment.
     */
    saveBatchCustomFieldValues(
        batchId: string,
        eventId: string,
        stationId: string,
        values: Array<{ custom_field_id: string; value: string | null }>
    ): Observable<BatchCustomFieldValue[]> {
        return this.http.post<BatchCustomFieldValue[]>(
            `${environment.api}/oee/batch/${batchId}/custom-fields`,
            { values, eventId, stationId }
        );
    }

    /**
     * Get the most recent event for an equipment (for determining current state)
     * Returns the latest production or downtime event
     */
    getLatestEvent(equipmentId: string): Observable<MachineEvent | null> {
        // Query for recent events (last 24 hours) with limit 1
        const now = new Date();
        const end = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(); // Tomorrow (to handle clock skew)
        const start = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago

        return this.getMachineEvents(equipmentId, start, end, undefined, 1000, 0).pipe(
            map((response: EventLogResponse) => {
                // Filter for production, batch and downtime events only
                const relevantEvents = response.events.filter(e =>
                    e.type === 'production' || e.type === 'batch' || e.type === 'downtime'
                );

                if (relevantEvents.length === 0) {
                    return null;
                }

                // Priority: Active events (no end_ts and not archived) should be returned first
                // Downtime takes priority over production (equipment is down if there's active downtime)
                const activeDowntime = relevantEvents.find(e => e.type === 'downtime' && !e.end_ts && !e.is_archived);
                if (activeDowntime) {
                    return activeDowntime;
                }

                const activeProduction = relevantEvents.find(e => (e.type === 'production' || e.type === 'batch') && !e.end_ts && !e.is_archived);
                if (activeProduction) {
                    return activeProduction;
                }

                // No active events - return the most recent by start_ts (will show as IDLE)
                relevantEvents.sort((a, b) => {
                    const timeA = new Date(a.start_ts || a.created_at).getTime();
                    const timeB = new Date(b.start_ts || b.created_at).getTime();
                    return timeB - timeA;
                });

                return relevantEvents[0];
            })
        );
    }

    /**
     * Get active downtime events (open events)
     */
    getActiveDowntimeEvents(equipmentId?: string | string[]): Observable<MachineEvent[]> {
        const id = Array.isArray(equipmentId) ? equipmentId[0] : equipmentId;
        if (!id) return of([]);

        // Query for recent downtime events and filter for open ones
        // We use a wide time range (e.g. 24 hours) to ensure we catch any ongoing downtime
        // We use a future end date to avoid clock skew issues where the event might be slightly "in the future" relative to client time
        const now = new Date();
        const end = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(); // Tomorrow
        const start = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago

        // Increase limit to 1000 to ensure we catch open events even if there's high activity in the last 24 hours
        return this.getMachineEvents(id, start, end, 'downtime', 1000, 0).pipe(
            map((response: EventLogResponse) => {
                // Filter for open events (no end_ts) and exclude archived events
                const openDowntime = response.events.filter(e => e.type === 'downtime' && !e.end_ts && !e.is_archived);
                return openDowntime;
            })
        );
    }

    /**
     * Edit an existing event
     */
    editEvent(request: EditEventRequest): Observable<MachineEvent> {
        // Pass all fields from request to backend, mapping legacy fields where necessary
        // The backend now supports aliases, but we map explicitly for compatibility
        const body = {
            ...request,
            start_time: request.start_ts,
            end_time: request.end_ts,
            downtime_reason_id: request.downtime_reason_id,
            comments: request.notes
        };

        // DEBUG: Log the request body being sent to backend
        console.log('[DEBUG] OEEDataService.editEvent - sending PUT request:', {
            url: `${environment.api}/oee/event/${request.event_id}`,
            body_keys: Object.keys(body),
            start_time: body.start_time,
            end_time: body.end_time,
            batch_number: body.batch_number,
            work_order: body.work_order
        });

        return this.http.put<MachineEvent>(`${environment.api}/oee/event/${request.event_id}`, body);
    }

    // --- Event-Batch Link Methods ---

    /**
     * Get batch events linked to a downtime/incident event
     */
    getEventBatchLinks(eventId: string): Observable<LinkedBatchEvent[]> {
        return this.http.get<any[]>(`${environment.api}/oee/event/${eventId}/batch-links`).pipe(
            map(rows => rows.map(r => ({
                batch_event_id: r.out_batch_event_id,
                batch_number: r.out_batch_number,
                work_order: r.out_work_order,
                equipment_id: r.out_equipment_id,
                equipment_name: r.out_equipment_name,
                start_ts: r.out_start_ts,
                end_ts: r.out_end_ts,
                status: r.out_status,
                good_parts: r.out_good_parts,
                scrap_parts: r.out_scrap_parts,
                total_parts: r.out_total_parts,
                link_notes: r.out_link_notes,
                linked_at: r.out_linked_at,
                linked_by: r.out_linked_by,
                is_dismissed: r.out_is_dismissed,
                auto_linked: r.out_is_auto_linked
            })))
        );
    }

    /**
     * Update batch events linked to a downtime/incident event
     */
    updateEventBatchLinks(eventId: string, batchEventIds: string[]): Observable<{ success: boolean; linked_batches: any[] }> {
        return this.http.put<{ success: boolean; linked_batches: any[] }>(
            `${environment.api}/oee/event/${eventId}/batch-links`,
            { batch_event_ids: batchEventIds }
        );
    }

    /**
     * Get downtime/incident events linked to a batch event
     */
    getBatchEventLinks(batchId: string): Observable<LinkedDowntimeIncidentEvent[]> {
        return this.http.get<any[]>(`${environment.api}/oee/batch/${batchId}/event-links`).pipe(
            map(rows => rows.map(r => ({
                event_id: r.out_event_id,
                event_type: r.out_event_type,
                equipment_id: r.out_equipment_id,
                equipment_name: r.out_equipment_name,
                start_ts: r.out_start_ts,
                end_ts: r.out_end_ts,
                duration_minutes: r.out_duration_minutes,
                downtime_reason: r.out_downtime_reason,
                incident_reason: r.out_incident_reason,
                notes: r.out_notes,
                link_notes: r.out_link_notes,
                linked_at: r.out_linked_at,
                is_dismissed: r.out_is_dismissed,
                auto_linked: r.out_is_auto_linked
            })))
        );
    }

    /**
     * Update downtime/incident events linked to a batch event
     */
    updateBatchEventLinks(batchId: string, eventIds: string[]): Observable<{ success: boolean; linked_events: any[] }> {
        return this.http.put<{ success: boolean; linked_events: any[] }>(
            `${environment.api}/oee/batch/${batchId}/event-links`,
            { event_ids: eventIds }
        );
    }

    getEventById(eventId: string): Observable<any> {
        return this.http.get<any>(`${environment.api}/oee/event/${eventId}`);
    }

    // ============================================
    // Batch Time Tracking
    // ============================================

    getBatchTimeEntries(batchId: string): Observable<BatchTimeEntry[]> {
        return this.http.get<BatchTimeEntry[]>(
            `${environment.api}/oee/batch/${batchId}/time-entries`
        );
    }

    getWorkerNotesForBatch(batchId: string): Observable<WorkerNote[]> {
        return this.http.get<WorkerNote[]>(
            `${environment.api}/oee/batch/${batchId}/worker-notes`
        );
    }

    startBatchTimeEntry(batchId: string, request: StartTimeEntryRequest): Observable<BatchTimeEntry> {
        return this.http.post<BatchTimeEntry>(
            `${environment.api}/oee/batch/${batchId}/time-entries`, request
        ).pipe(
            tap(() => this.batchTimeEntriesChangedSubject.next())
        );
    }

    stopBatchTimeEntry(timeEntryId: string, endTs?: string): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(
            `${environment.api}/oee/batch/time-entry/${timeEntryId}/stop`,
            { end_ts: endTs }
        ).pipe(
            tap(() => this.batchTimeEntriesChangedSubject.next())
        );
    }

    updateBatchTimeEntry(
        timeEntryId: string,
        updates: { start_ts?: string; end_ts?: string; duration_override_minutes?: number; notes?: string; is_rework?: boolean | null; confirm_retract?: boolean }
    ): Observable<BatchTimeEntry> {
        return this.http.put<BatchTimeEntry>(
            `${environment.api}/oee/batch/time-entry/${timeEntryId}`, updates
        ).pipe(
            tap(() => this.batchTimeEntriesChangedSubject.next())
        );
    }

    updateLaborEntry(
        entryId: string,
        updates: { clock_in_ts?: string; clock_out_ts?: string | null; tags?: string[] | null; notes?: string | null; batch_id?: string | null }
    ): Observable<unknown> {
        return this.http.put<unknown>(
            `${environment.api}/ops/labor/${entryId}`, updates
        ).pipe(
            tap(() => this.batchTimeEntriesChangedSubject.next())
        );
    }

    deleteBatchTimeEntry(timeEntryId: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(
            `${environment.api}/oee/batch/time-entry/${timeEntryId}`
        ).pipe(
            tap(() => this.batchTimeEntriesChangedSubject.next())
        );
    }

    getActiveTimeEntries(userId?: string): Observable<ActiveTimeEntry[]> {
        const params: Record<string, string> = {};
        if (userId) params['user_id'] = userId;
        return this.http.get<ActiveTimeEntry[]>(
            `${environment.api}/oee/time-entries/active`, { params }
        );
    }

    /**
     * Start troubleshooting a downtime event
     */
    startTroubleshooting(request: StartTroubleshootingRequest): Observable<MachineEvent> {
        return this.http.post<MachineEvent>(`${environment.api}/oee/event/troubleshoot`, request).pipe(
            map(response => ({
                ...response,
                event_id: request.event_id,
            } as MachineEvent))
        );
    }

    /**
     * Resolve a downtime error
     */
    resolveError(request: ResolveErrorRequest): Observable<MachineEvent> {
        const body: Record<string, unknown> = {
            notes: request.notes,
            troubleshooting_notes: request.troubleshooting_notes,
            resolution_notes: request.resolution_notes,
            resolved_by: request.resolved_by
        };

        // Include resolved_at and end_ts if provided
        if (request.resolved_at) {
            body['resolved_at'] = request.resolved_at;
        }
        if (request.end_ts) {
            body['end_ts'] = request.end_ts;
        }

        return this.http.put<MachineEvent>(`${environment.api}/oee/event/${request.event_id}`, body);
    }

    /**
     * Archive an event (soft delete)
     */
    archiveEvent(request: ArchiveEventRequest): Observable<MachineEvent> {
        const body = {
            archive_reason: request.archive_reason
        };
        return this.http.delete<MachineEvent>(`${environment.api}/oee/event/${request.event_id}`, { body });
    }

    /**
     * Unarchive an event (restore)
     */
    unarchiveEvent(request: UnarchiveEventRequest): Observable<MachineEvent> {
        // Real API call to restore the event
        return this.http.patch<{ success: boolean; event: MachineEvent; message: string }>(
            `${environment.api}/oee/event/${request.event_id}/restore`,
            {}
        ).pipe(
            map(response => response.event)
        );
    }

    // ============================================
    // Equipment Archive Methods
    // ============================================

    /**
     * Archive equipment (soft delete)
     * Equipment must not be in RUNNING or DOWN state
     */
    archiveEquipment(request: ArchiveEquipmentRequest): Observable<Equipment> {
        const body = {
            archive_reason: request.archive_reason
        };
        return this.http.delete<Equipment>(`${environment.api}/oee/equipment/${request.equipment_id}`, { body });
    }

    /**
     * Unarchive equipment (restore)
     */
    unarchiveEquipment(request: UnarchiveEquipmentRequest): Observable<Equipment> {
        // Real API call to restore the equipment
        return this.http.patch<{ success: boolean; equipment: Equipment; message: string }>(
            `${environment.api}/oee/equipment/${request.equipment_id}/restore`,
            {}
        ).pipe(
            map(response => response.equipment)
        );
    }

    // ============================================
    // User Management Methods
    // ============================================

    getOrganizationUsers(): Observable<{ id: string; name: string; email: string; username: string; employee_number?: string }[]> {
        return this.http.get<{ id: string; name: string; email: string; username: string; employee_number?: string }[]>(
            `${environment.api}/users/organization`
        );
    }

    // ============================================
    // Analytics API Methods (Real Backend)
    // ============================================

    /**
     * API response shape for analytics series endpoint
     */
    private interface_AnalyticsSeriesResponse = null; // Type placeholder, actual type defined inline

    /**
     * Get time-series analytics data from the backend
     * Returns interval-grouped metrics per time interval
     *
     * @param equipmentIds Array of equipment UUIDs
     * @param startTime ISO timestamp for range start
     * @param endTime ISO timestamp for range end
     * @param interval Time interval for grouping: '1h' | '1d' | '1w' | '1m'
     * @param metric The metric to calculate: 'oee' | 'availability' | 'performance' | 'quality' | 'downtime' | 'mtbf' | 'mttr' | 'scrap_rate'
     * @param calcMode Calculation mode override: 'standard' | 'strict' | 'teep' (optional, defaults to site setting)
     * @param downtimeTypes Types to include: ['unplanned', 'planned', 'idle']
     * @param downtimeReasonIds Specific reason IDs to filter by (optional)
     * @param timezone IANA timezone used to align calendar buckets (optional)
     * @returns Observable of ChartSeries[] for rendering in line charts
     */
    getAnalyticsSeries(
        equipmentIds: string[],
        startTime: string,
        endTime: string,
        interval: TimeInterval,
        metric: AnalyticsMetric,
        calcMode?: CalculationMode,
        downtimeTypes: DowntimeReasonType[] = ['unplanned', 'planned', 'idle'],
        downtimeReasonIds?: string[],
        stationIds?: string[],
        productIds?: string[],
        timezone?: string
    ): Observable<{ series: ChartSeries[] }> {
        // Build query params
        let params = new HttpParams()
            .set('start_time', startTime)
            .set('end_time', endTime)
            .set('interval', interval)
            .set('metric', metric)
            .set('downtime_types', downtimeTypes.join(','));

        if (equipmentIds.length > 0) {
            params = params.set('equipment_ids', equipmentIds.join(','));
        }
        if (calcMode) {
            params = params.set('calc_mode', calcMode);
        }
        if (downtimeReasonIds && downtimeReasonIds.length > 0) {
            params = params.set('downtime_reason_ids', downtimeReasonIds.join(','));
        }
        if (stationIds && stationIds.length > 0) {
            params = params.set('station_ids', stationIds.join(','));
        }
        if (productIds && productIds.length > 0) {
            params = params.set('product_ids', productIds.join(','));
        }
        if (timezone) {
            params = params.set('timezone', timezone);
        }

        return this.http.get<{
            series: {
                id: string;
                name: string;
                series: { name: string; value: number | null }[];
            }[];
        }>(`${environment.api}/oee/analytics/series`, { params }).pipe(
            map(response => {
                const mappedSeries = response.series.map(equip => ({
                    id: equip.id,
                    name: equip.name,
                    series: equip.series
                        .map(point => {
                            const roundVal = (v: number | null | undefined) =>
                                v !== null && v !== undefined ? Math.round(v * 100) / 100 : null;
                            return {
                                name: point.name,
                                value: roundVal(point.value)
                            };
                        })
                }));

                return {
                    series: mappedSeries
                };
            })
        );
    }

    /**
     * Get Pareto chart data from the backend
     * Returns aggregated data sorted by value with cumulative percentages
     * Supports both downtime analysis and batch analysis
     *
     * @param equipmentIds Array of equipment UUIDs
     * @param startTime ISO timestamp for range start
     * @param endTime ISO timestamp for range end
     * @param categoryMetric How to group data: 'downtime_reason' | 'downtime_type' | 'equipment' | 'shift' | 'batch'
     * @param valueMetric What to measure: 'duration' | 'count' | 'frequency' | 'batch_count' | 'batch_duration'
     * @param downtimeTypes Types to include: ['unplanned', 'planned', 'idle'] (applies to downtime analysis only)
     * @param downtimeReasonIds Specific reason IDs to filter by (optional, applies to downtime analysis only)
     * @returns Observable of Pareto data for rendering
     */
    getAnalyticsPareto(
        equipmentIds: string[],
        startTime: string,
        endTime: string,
        categoryMetric: ParetoCategoryMetric,
        valueMetric: ParetoValueMetric,
        downtimeTypes: DowntimeReasonType[] = ['unplanned', 'planned', 'idle'],
        downtimeReasonIds?: string[],
        stationIds?: string[],
        productIds?: string[]
    ): Observable<{ data: ParetoDataPoint[]; cumulativeData: { name: string; value: number }[]; totalValue: number }> {
        // Build query params
        let params = new HttpParams()
            .set('equipment_ids', equipmentIds.join(','))
            .set('start_time', startTime)
            .set('end_time', endTime)
            .set('category', categoryMetric)
            .set('value', valueMetric)
            .set('downtime_types', downtimeTypes.join(','));

        if (downtimeReasonIds && downtimeReasonIds.length > 0) {
            params = params.set('downtime_reason_ids', downtimeReasonIds.join(','));
        }
        if (stationIds && stationIds.length > 0) {
            params = params.set('station_ids', stationIds.join(','));
        }
        if (productIds && productIds.length > 0) {
            params = params.set('product_ids', productIds.join(','));
        }

        return this.http.get<{
            data: {
                name: string;
                value: number;
                type: string | null;
                percentage: number;
                cumulativePercentage: number;
                extra?: { id?: string; type?: string };
            }[];
            totalValue: number;
        }>(`${environment.api}/oee/analytics/pareto`, { params }).pipe(
            map(response => {
                // Build cumulative data array for chart line
                const cumulativeData = response.data.map(item => ({
                    name: item.name,
                    value: item.cumulativePercentage
                }));

                // Transform to ParetoDataPoint format
                const data: ParetoDataPoint[] = response.data.map(item => ({
                    name: item.name,
                    value: item.value,
                    color: this.getDowntimeTypeColor(item.type),
                    extra: {
                        id: item.extra?.id,
                        type: item.type || undefined,
                        percentage: item.percentage,
                        cumulativePercentage: item.cumulativePercentage
                    }
                }));

                return {
                    data,
                    cumulativeData,
                    totalValue: response.totalValue
                };
            })
        );
    }

    /**
     * Get color for downtime type (for Pareto chart bars)
     */
    private getDowntimeTypeColor(type: string | null | undefined): string {
        switch (type) {
            case 'unplanned':
                return '#EF4444'; // Red
            case 'planned':
                return '#3B82F6'; // Blue
            case 'idle':
                return '#9CA3AF'; // Gray
            default:
                return '#6B7280'; // Default gray
        }
    }

    /**
     * Get the timestamp span of all non-archived machine events for the
     * given equipment. Returned start/end power the chart "Max" range so
     * charts span only the period that has real data.
     * Returns nulls when the equipment has no events at all.
     */
    getEquipmentDataRange(
        equipmentIds: string[]
    ): Observable<{ minTs: string | null; maxTs: string | null }> {
        const params = new HttpParams().set('equipment_ids', equipmentIds.join(','));
        return this.http
            .get<{ min_ts: string | null; max_ts: string | null }>(
                `${environment.api}/oee/analytics/data-range`,
                { params }
            )
            .pipe(map(r => ({ minTs: r.min_ts, maxTs: r.max_ts })));
    }

    // ============================================
    // Incident Analytics Methods
    // ============================================

    /**
     * Get incident time-series analytics data
     * @param equipmentIds Equipment to include
     * @param startTime Start of time range (ISO string)
     * @param endTime End of time range (ISO string)
     * @param interval Time grouping interval
     * @param metric Incident metric to calculate
     * @param timezone IANA timezone used to align calendar buckets (optional)
     */
    getIncidentAnalyticsSeries(
        equipmentIds: string[],
        startTime: string,
        endTime: string,
        interval: TimeInterval,
        metric: 'incident_count' | 'incident_duration',
        incidentReasonIds?: string[],
        stationIds?: string[],
        timezone?: string
    ): Observable<ChartSeries[]> {
        return this.http.post<{
            series: {
                id: string;
                name: string;
                series: { name: string; value: number }[];
            }[];
        }>(`${environment.api}/oee/analytics/incident-series`, {
            equipment_ids: equipmentIds,
            start_time: startTime,
            end_time: endTime,
            interval,
            metric,
            ...(incidentReasonIds?.length ? { incident_reason_ids: incidentReasonIds } : {}),
            ...(stationIds?.length ? { station_ids: stationIds } : {}),
            ...(timezone ? { timezone } : {})
        }).pipe(
            map(response => response.series.map(equip => ({
                id: equip.id,
                name: equip.name,
                series: equip.series.map(point => ({
                    name: point.name,
                    value: point.value
                }))
            })))
        );
    }

    /**
     * Get incident Pareto chart data
     * @param equipmentIds Equipment to include
     * @param startTime Start of time range (ISO string)
     * @param endTime End of time range (ISO string)
     * @param categoryMetric How to group incidents
     * @param valueMetric What to measure
     */
    getIncidentAnalyticsPareto(
        equipmentIds: string[],
        startTime: string,
        endTime: string,
        categoryMetric: 'incident_reason' | 'incident_category' | 'equipment' | 'product' | 'product_family',
        valueMetric: 'count' | 'duration' | 'frequency',
        incidentReasonIds?: string[],
        stationIds?: string[],
        productIds?: string[]
    ): Observable<{ data: ParetoDataPoint[]; cumulativeData: { name: string; value: number }[]; totalValue: number }> {
        return this.http.post<{
            data: {
                name: string;
                value: number;
                percentage: number;
                cumulativePercentage: number;
                extra?: { id?: string; category?: string };
            }[];
            totalValue: number;
        }>(`${environment.api}/oee/analytics/incident-pareto`, {
            equipment_ids: equipmentIds,
            start_time: startTime,
            end_time: endTime,
            category_metric: categoryMetric,
            value_metric: valueMetric,
            ...(incidentReasonIds?.length ? { incident_reason_ids: incidentReasonIds } : {}),
            ...(stationIds?.length ? { station_ids: stationIds } : {}),
            ...(productIds?.length ? { product_ids: productIds } : {})
        }).pipe(
            map(response => {
                const cumulativeData = response.data.map(item => ({
                    name: item.name,
                    value: item.cumulativePercentage
                }));

                const data: ParetoDataPoint[] = response.data.map(item => ({
                    name: item.name,
                    value: item.value,
                    color: '#F59E0B', // Amber for incidents
                    extra: {
                        id: item.extra?.id,
                        type: item.extra?.category,
                        percentage: item.percentage,
                        cumulativePercentage: item.cumulativePercentage
                    }
                }));

                return {
                    data,
                    cumulativeData,
                    totalValue: response.totalValue
                };
            })
        );
    }

    // ============================================
    // Pareto Chart Data Methods
    // ============================================

    /**
     * Generate Pareto chart data for downtime analysis
     * Aggregates downtime events by the specified category metric
     */
    generateParetoData(
        events: MachineEvent[],
        categoryMetric: 'downtime_reason' | 'downtime_type' | 'equipment' | 'shift',
        valueMetric: 'duration' | 'count' | 'frequency',
        startDate: Date,
        endDate: Date
    ): { data: ParetoDataPoint[]; cumulativeData: { name: string; value: number }[]; totalValue: number } {
        // Filter to downtime events only
        const downtimeEvents = events.filter(e => e.type === 'downtime') as DowntimeEvent[];

        if (downtimeEvents.length === 0) {
            return { data: [], cumulativeData: [], totalValue: 0 };
        }

        // Aggregate by category
        const aggregation = new Map<string, {
            value: number;
            count: number;
            color?: string;
            id?: string;
            type?: string;
        }>();

        const daysDiff = Math.max(1, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

        for (const event of downtimeEvents) {
            let key: string;
            let color: string | undefined;
            let id: string | undefined;
            let type: string | undefined;

            switch (categoryMetric) {
                case 'downtime_reason':
                    key = event.downtime_reason?.display_name || 'Unknown Reason';
                    color = event.downtime_reason?.color_hex;
                    id = event.downtime_reason_id;
                    type = event.downtime_reason_type;
                    break;
                case 'downtime_type':
                    type = event.downtime_reason_type || 'unknown';
                    key = this.formatDowntimeType(type);
                    color = this.getDowntimeTypeColor(type);
                    break;
                case 'equipment':
                    key = event.equipment_name || event.equipment_code || event.equipment_id;
                    break;
                case 'shift':
                    key = this.getShiftFromTimestamp(event.start_ts);
                    break;
                default:
                    key = 'Unknown';
            }

            const existing = aggregation.get(key) || { value: 0, count: 0 };
            const duration = event.duration_minutes || 0;

            existing.value += duration;
            existing.count += 1;
            if (color) existing.color = color;
            if (id) existing.id = id;
            if (type) existing.type = type;

            aggregation.set(key, existing);
        }

        // Convert to array and calculate value based on metric
        let data: ParetoDataPoint[] = [];
        let totalValue = 0;

        aggregation.forEach((agg, name) => {
            let value: number;
            switch (valueMetric) {
                case 'duration':
                    value = agg.value;
                    break;
                case 'count':
                    value = agg.count;
                    break;
                case 'frequency':
                    value = agg.count / daysDiff;
                    break;
                default:
                    value = agg.value;
            }

            totalValue += value;
            data.push({
                name,
                value,
                color: agg.color,
                extra: {
                    id: agg.id,
                    type: agg.type
                }
            });
        });

        // Sort descending by value (Pareto principle)
        data.sort((a, b) => b.value - a.value);

        // Calculate percentages and cumulative
        let cumulative = 0;
        const cumulativeData: { name: string; value: number }[] = [];

        data = data.map(item => {
            const percentage = totalValue > 0 ? (item.value / totalValue) * 100 : 0;
            cumulative += percentage;

            cumulativeData.push({
                name: item.name,
                value: Math.min(100, cumulative)
            });

            return {
                ...item,
                extra: {
                    ...item.extra,
                    percentage,
                    cumulativePercentage: cumulative
                }
            };
        });

        return { data, cumulativeData, totalValue };
    }

    /**
     * Generate mock Pareto data for demonstration
     */
    generateMockParetoData(
        categoryMetric: 'downtime_reason' | 'downtime_type' | 'equipment' | 'shift',
        valueMetric: 'duration' | 'count' | 'frequency'
    ): { data: ParetoDataPoint[]; cumulativeData: { name: string; value: number }[]; totalValue: number } {
        // Create realistic mock data based on category
        let rawData: { name: string; value: number; color?: string }[] = [];

        switch (categoryMetric) {
            case 'downtime_reason':
                rawData = [
                    { name: 'Mechanical Failure', value: 450, color: '#EF4444' },
                    { name: 'Material Shortage', value: 320, color: '#F59E0B' },
                    { name: 'Operator Error', value: 180, color: '#3B82F6' },
                    { name: 'Scheduled Maintenance', value: 150, color: '#10B981' },
                    { name: 'Changeover', value: 120, color: '#8B5CF6' },
                    { name: 'Quality Issue', value: 90, color: '#EC4899' },
                    { name: 'Tool Change', value: 60, color: '#6B7280' },
                    { name: 'Power Outage', value: 30, color: '#1F2937' }
                ];
                break;
            case 'downtime_type':
                rawData = [
                    { name: 'Unplanned', value: 720, color: '#EF4444' },
                    { name: 'Planned', value: 420, color: '#F59E0B' },
                    { name: 'Idle', value: 260, color: '#6B7280' }
                ];
                break;
            case 'equipment':
                rawData = [
                    { name: 'CNC Machine #1', value: 380 },
                    { name: 'Assembly Line A', value: 290 },
                    { name: 'Press #3', value: 220 },
                    { name: 'Packaging Unit', value: 180 },
                    { name: 'Conveyor B', value: 130 }
                ];
                break;
            case 'shift':
                rawData = [
                    { name: 'Night Shift', value: 520 },
                    { name: 'Day Shift', value: 380 },
                    { name: 'Evening Shift', value: 300 }
                ];
                break;
        }

        // Adjust values based on metric type
        if (valueMetric === 'count') {
            rawData = rawData.map(d => ({ ...d, value: Math.floor(d.value / 30) }));
        } else if (valueMetric === 'frequency') {
            rawData = rawData.map(d => ({ ...d, value: Math.round(d.value / 30 * 10) / 10 }));
        }

        // Sort descending
        rawData.sort((a, b) => b.value - a.value);

        const totalValue = rawData.reduce((sum, d) => sum + d.value, 0);
        let cumulative = 0;
        const cumulativeData: { name: string; value: number }[] = [];

        const data: ParetoDataPoint[] = rawData.map(item => {
            const percentage = totalValue > 0 ? (item.value / totalValue) * 100 : 0;
            cumulative += percentage;

            cumulativeData.push({
                name: item.name,
                value: Math.min(100, cumulative)
            });

            return {
                name: item.name,
                value: item.value,
                color: item.color,
                extra: {
                    percentage,
                    cumulativePercentage: cumulative
                }
            };
        });

        return { data, cumulativeData, totalValue };
    }

    /**
     * Format downtime type for display
     */
    private formatDowntimeType(type: string): string {
        switch (type) {
            case 'unplanned': return 'Unplanned';
            case 'planned': return 'Planned';
            case 'idle': return 'Idle';
            default: return type.charAt(0).toUpperCase() + type.slice(1);
        }
    }

    /**
     * Determine shift from timestamp
     */
    private getShiftFromTimestamp(timestamp: string): string {
        const hour = new Date(timestamp).getHours();
        if (hour >= 6 && hour < 14) return 'Day Shift';
        if (hour >= 14 && hour < 22) return 'Evening Shift';
        return 'Night Shift';
    }

    // ============================================
    // Analytics Chart CRUD Methods
    // ============================================

    /**
     * Stored chart configuration returned from API
     */


    /**
     * Get all analytics charts for a site
     */
    getAnalyticsCharts(siteId: string): Observable<StoredChartConfig[]> {
        return this.http.get<{ charts: StoredChartConfig[] }>(
            `${environment.api}/oee/analytics/charts/${siteId}`
        ).pipe(map(response => response.charts));
    }

    /**
     * Create a new analytics chart
     */
    createAnalyticsChart(chart: CreateChartRequest): Observable<StoredChartConfig> {
        return this.http.post<{ chart: StoredChartConfig }>(
            `${environment.api}/oee/analytics/charts`,
            chart
        ).pipe(map(response => response.chart));
    }

    /**
     * Update an existing analytics chart
     */
    updateAnalyticsChart(chartId: string, updates: UpdateChartRequest): Observable<StoredChartConfig> {
        return this.http.put<{ chart: StoredChartConfig }>(
            `${environment.api}/oee/analytics/charts/${chartId}`,
            updates
        ).pipe(map(response => response.chart));
    }

    /**
     * Delete an analytics chart
     */
    deleteAnalyticsChart(chartId: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(
            `${environment.api}/oee/analytics/charts/${chartId}`
        );
    }

    /**
     * Reorder analytics charts
     */
    reorderAnalyticsCharts(siteId: string, chartOrders: { id: string; order: number }[]): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(
            `${environment.api}/oee/analytics/charts/reorder`,
            { siteId, chartOrders }
        );
    }

    /**
     * Update chart layout (column span) - lightweight update for resize operations
     */
    updateAnalyticsChartLayout(chartId: string, columnSpan: number): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(
            `${environment.api}/oee/analytics/charts/${chartId}/layout`,
            { columnSpan }
        );
    }

    /**
     * Export machine events to CSV
     * @param config Export configuration from the export modal
     * @returns Observable that completes when download is triggered
     */
    exportEvents(config: {
        siteId: string;
        equipmentIds: string[] | null;
        startTime: string | null;
        endTime: string | null;
        eventTypes: string[] | null;
        downtimeTypes: string[] | null;
        downtimeReasonIds: string[] | null;
        incidentReasonIds: string[] | null;
        loggedByUserIds: string[] | null;
        includeArchived: boolean;
        dateFormat: string;
        timezone: string;
    }): Observable<void> {
        let params = new HttpParams();

        if (config.equipmentIds?.length) {
            params = params.set('equipment_ids', config.equipmentIds.join(','));
        }
        if (config.startTime) {
            params = params.set('start_time', config.startTime);
        }
        if (config.endTime) {
            params = params.set('end_time', config.endTime);
        }
        if (config.eventTypes?.length) {
            params = params.set('event_types', config.eventTypes.join(','));
        }
        if (config.downtimeTypes?.length) {
            params = params.set('downtime_types', config.downtimeTypes.join(','));
        }
        if (config.downtimeReasonIds?.length) {
            params = params.set('downtime_reason_ids', config.downtimeReasonIds.join(','));
        }
        if (config.incidentReasonIds?.length) {
            params = params.set('incident_reason_ids', config.incidentReasonIds.join(','));
        }
        if (config.loggedByUserIds?.length) {
            params = params.set('logged_by_user_ids', config.loggedByUserIds.join(','));
        }
        params = params.set('include_archived', String(config.includeArchived));
        params = params.set('date_format', config.dateFormat);
        params = params.set('timezone', config.timezone);

        return this.http.get(`${environment.api}/oee/sites/${config.siteId}/events/export`, {
            params,
            responseType: 'blob',
            observe: 'response'
        }).pipe(
            tap(response => {
                // Extract filename from Content-Disposition header if available
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = 'events_export.csv';

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
                    if (match?.[1]) {
                        filename = match[1];
                    }
                }

                if (response.body) {
                    saveAs(response.body, filename);
                }
            }),
            map(() => void 0)
        );
    }

    // ============================================
    // Batch History Methods
    // ============================================

    /**
     * Get batch history for a site with OEE metrics
     * @param siteId Site ID
     * @param startTime Start of time range (ISO string)
     * @param endTime End of time range (ISO string)
     * @param page Page number (0-indexed)
     * @param pageSize Number of items per page
     * @returns Observable of batch history response with pagination info
     */
    getSiteBatchHistory(
        siteId: string,
        startTime: string,
        endTime: string,
        page: number = 0,
        pageSize: number = 50
    ): Observable<BatchHistoryResponse> {
        const params = new HttpParams()
            .set('startTime', startTime)
            .set('endTime', endTime)
            .set('limit', pageSize.toString())
            .set('offset', (page * pageSize).toString());

        return this.http.get<BatchHistoryResponse>(
            `${environment.api}/oee/sites/${siteId}/batch-history`,
            { params }
        );
    }

    /**
     * Get the focused per-batch payload for /oee/job-history/:batchId.
     * 404 -> Observable error; consumers should handle.
     */
    getBatchFocus(
        siteId: string,
        batchNumber: string
    ): Observable<BatchFocusResponse> {
        return this.http.get<BatchFocusResponse>(
            `${environment.api}/oee/sites/${siteId}/job-focus/${encodeURIComponent(batchNumber)}`
        );
    }

    /**
     * Get all batch runs for a specific batch name (regardless of time range)
     * Used when expanding a batch group to see all historical runs
     *
     * @param siteId Site ID
     * @param batchNumber The batch name/number to fetch runs for
     * @param offset Pagination offset
     * @param limit Number of items to fetch
     * @returns Observable of batch history response with all runs for this batch name
     */
    getBatchesByName(
        siteId: string,
        batchNumber: string,
        offset: number = 0,
        limit: number = 50
    ): Observable<BatchHistoryResponse> {
        const params = new HttpParams()
            .set('limit', limit.toString())
            .set('offset', offset.toString());

        return this.http.get<BatchHistoryResponse>(
            `${environment.api}/oee/sites/${siteId}/batches/by-name/${encodeURIComponent(batchNumber)}`,
            { params }
        );
    }

    /**
     * Site-wide job search for the Job History quick-search filter.
     * Returns distinct batch_number rows across all time, with optional in-range
     * counts so callers can tell whether a match has activity in the current window.
     */
    searchSiteJobs(
        siteId: string,
        search: string | null,
        inRangeStart: string | null = null,
        inRangeEnd: string | null = null,
        limit: number = 50
    ): Observable<SiteJobSearchResponse> {
        let params = new HttpParams().set('limit', limit.toString());
        if (search && search.trim().length > 0) {
            params = params.set('search', search.trim());
        }
        if (inRangeStart) params = params.set('inRangeStart', inRangeStart);
        if (inRangeEnd) params = params.set('inRangeEnd', inRangeEnd);

        return this.http.get<SiteJobSearchResponse>(
            `${environment.api}/oee/sites/${siteId}/jobs`,
            { params }
        );
    }

    // ============================================
    // Time Range State Methods
    // ============================================

    /**
     * Get the currently selected time range
     */
    getSelectedTimeRange(): TimeRangeOption {
        return this.selectedTimeRangeSubject.getValue();
    }

    /**
     * Set the selected time range (persists to localStorage)
     */
    setSelectedTimeRange(range: TimeRangeOption): void {
        localStorage.setItem(this.TIME_RANGE_STORAGE_KEY, range);
        this.selectedTimeRangeSubject.next(range);
    }

    setCustomTimeRange(start: string | null, end: string | null): void {
        if (start) {
            localStorage.setItem(this.CUSTOM_RANGE_START_STORAGE_KEY, start);
        } else {
            localStorage.removeItem(this.CUSTOM_RANGE_START_STORAGE_KEY);
        }
        if (end) {
            localStorage.setItem(this.CUSTOM_RANGE_END_STORAGE_KEY, end);
        } else {
            localStorage.removeItem(this.CUSTOM_RANGE_END_STORAGE_KEY);
        }
    }

    getCustomTimeRange(): { start: string | null; end: string | null } {
        return {
            start: localStorage.getItem(this.CUSTOM_RANGE_START_STORAGE_KEY),
            end: localStorage.getItem(this.CUSTOM_RANGE_END_STORAGE_KEY)
        };
    }

    /**
     * Convert a TimeRangeOption to actual start/end Date objects
     * Uses local timezone for date calculations
     */
    getSelectedTimeRangeDates(): { start: Date; end: Date } {
        const range = this.selectedTimeRangeSubject.getValue();
        const now = new Date();
        let start: Date;
        let end: Date = now;

        switch (range) {
            case 'today':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                break;

            case 'yesterday':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
                break;

            case 'last_24_hours':
                start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                end = now;
                break;

            case 'last_7_days':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                break;

            case 'last_30_days':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                break;

            case 'last_3_months':
                const targetMonth = new Date(now.getFullYear(), now.getMonth() - 3, 1);
                const lastDayOfTargetMonth = new Date(
                    targetMonth.getFullYear(),
                    targetMonth.getMonth() + 1,
                    0
                ).getDate();
                start = new Date(
                    targetMonth.getFullYear(),
                    targetMonth.getMonth(),
                    Math.min(now.getDate(), lastDayOfTargetMonth),
                    0,
                    0,
                    0,
                    0
                );
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                break;

            case 'this_week': {
                // Week starts on Monday (day 1)
                const dayOfWeek = now.getDay();
                const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff, 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                break;
            }

            case 'this_month':
                start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                break;

            case 'last_month':
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999); // Last day of prev month
                break;

            case 'custom': {
                const { start: customStart, end: customEnd } = this.getCustomTimeRange();
                if (customStart) {
                    start = new Date(`${customStart}T00:00:00`);
                } else {
                    start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
                }
                if (customEnd) {
                    end = new Date(`${customEnd}T23:59:59.999`);
                } else {
                    end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
                }
                break;
            }

            default:
                // Default to last 7 days
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        }

        return { start, end };
    }

    /**
     * Parse a local datetime-local input value to an ISO string
     * The input provides local time, we convert to ISO for API calls
     */
    parseLocalDateTimeToISO(localDateTimeValue: string): string {
        // datetime-local gives us "YYYY-MM-DDTHH:mm" in local time
        // Create a Date from this local time and convert to ISO
        const date = new Date(localDateTimeValue);
        return date.toISOString();
    }

    /**
     * Format an ISO date string for datetime-local input
     * Returns "YYYY-MM-DDTHH:mm" in local time
     */
    formatISOForDateTimeLocal(isoString: string): string {
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
}

/**
 * Stored chart configuration from backend
 */
export interface StoredChartConfig {
    id: string;
    siteId: string;
    title: string;
    chartType: 'line' | 'pareto';
    metric: AnalyticsMetric;
    calculationMode: CalculationMode;
    interval: TimeInterval;
    dateRange: string;
    customStartDate: string | null;
    customEndDate: string | null;
    equipmentIds: string[];
    stationIds: string[];
    selectionMode: 'equipment' | 'station' | null;
    downtimeTypes: DowntimeReasonType[];
    downtimeReasonIds: string[];
    incidentReasonIds: string[];
    productIds: string[];
    paretoDataSource: 'downtime' | 'incident' | 'batch' | null;
    paretoCategoryMetric: ParetoCategoryMetric | null;
    paretoValueMetric: ParetoValueMetric | null;
    durationUnit: string | null;
    showLegend: boolean;
    yAxisMin: number | null;
    yAxisMax: number | null;
    order: number;
    columnSpan: number;
    createdAt: string;
    updatedAt: string;
}

/**
 * Request to create a new chart
 */
export interface CreateChartRequest {
    siteId: string;
    title: string;
    chartType: 'line' | 'pareto';
    metric: AnalyticsMetric;
    calculationMode?: CalculationMode;
    interval?: TimeInterval;
    dateRange?: string;
    customStartDate?: string | null;
    customEndDate?: string | null;
    equipmentIds?: string[];
    stationIds?: string[];
    selectionMode?: 'equipment' | 'station';
    downtimeTypes?: DowntimeReasonType[];
    downtimeReasonIds?: string[];
    incidentReasonIds?: string[];
    productIds?: string[];
    paretoDataSource?: 'downtime' | 'incident' | 'batch';
    paretoCategoryMetric?: ParetoCategoryMetric;
    paretoValueMetric?: ParetoValueMetric;
    durationUnit?: string;
    showLegend?: boolean;
    yAxisMin?: number | null;
    yAxisMax?: number | null;
    order?: number;
    columnSpan?: number;
}

/**
 * Request to update a chart
 */
export interface UpdateChartRequest {
    title?: string;
    chartType?: 'line' | 'pareto';
    metric?: AnalyticsMetric;
    calculationMode?: CalculationMode;
    interval?: TimeInterval;
    dateRange?: string;
    customStartDate?: string | null;
    customEndDate?: string | null;
    equipmentIds?: string[];
    stationIds?: string[];
    selectionMode?: 'equipment' | 'station';
    downtimeTypes?: DowntimeReasonType[];
    downtimeReasonIds?: string[];
    incidentReasonIds?: string[];
    productIds?: string[];
    paretoDataSource?: 'downtime' | 'incident' | 'batch';
    paretoCategoryMetric?: ParetoCategoryMetric;
    paretoValueMetric?: ParetoValueMetric;
    durationUnit?: string;
    showLegend?: boolean;
    yAxisMin?: number | null;
    yAxisMax?: number | null;
    order?: number;
    columnSpan?: number;
}
